(() => {
  const ELEMENT_NAME = 'nw-first-order-exit-offer';
  const registry = window.customElements;

  if (!registry || registry.get(ELEMENT_NAME)) return;

  const CAPTURE_SUPPRESSION_MS = 30 * 24 * 60 * 60 * 1000;
  // Owner, 2026-10-24: every close keeps the offer away for 14 days, on every device.
  const DISMISS_SUPPRESSION_MS = 14 * 24 * 60 * 60 * 1000;
  const RETURN_WINDOW_MS = 24 * 60 * 60 * 1000;
  // Owner, 2026-10-24: one block, one tab, three rules, the same on every page and
  // device. (1) The block opens once per visit, on the second page view or after
  // 30 s, whichever comes first. (2) A close leaves a quiet "10% OFF" tab, which
  // opens the same block. (3) After a close the block does not open on its own for
  // 14 days, and the tab stays until sign-up.
  const VISIT_TRIGGER = 'visit';
  const OFFER_TAB_TRIGGER = 'offer-tab';
  const VISIT_DELAY_MS = 30000;
  // Owner, 2026-10-25 (Igor): from 750px with a mouse or trackpad the block is a
  // centred pop-up, 560px wide. Phones and touch tablets keep the floating card,
  // the viewport less a 12px inset.
  const MODAL_WIDTH = 560;
  const NATIVE_PHONE_QUERY = '(max-width: 749px), (any-pointer: coarse)';
  // The most of a phone screen the sheet may take before it scrolls inside.
  const PHONE_SHEET_SHARE = 0.47;
  // The phone photo strip (CSS: --nw-native-strip-height, 120px). On a phone too
  // short for the whole email step it gives up height, down to the floor.
  const PHONE_STRIP_HEIGHT = 120;
  const PHONE_STRIP_MIN_HEIGHT = 80;
  // How long the clear layer outlives a tap that closed the block, to catch the click
  // that follows it (iOS sends it a few hundred ms after the finger lifts).
  const NATIVE_SCRIM_LINGER_MS = 700;
  const VISIT_SESSION_KEY = 'nw-native-welcome-visit-v1';
  const FORM_OPEN_TIMEOUT_MS = 8000;
  const SMS_ELIGIBILITY_TIMEOUT_MS = 8000;
  const SMS_ELIGIBILITY_MAX_MS = 15 * 60 * 1000;
  const EMAIL_AUTH_RETURN_KEY = 'nw-native-welcome-auth-return-v1';
  const EMAIL_AUTH_RETURN_MAX_MS = 15 * 60 * 1000;
  const SMS_STATUS_POLL_MS = 8000;
  const SMS_STATUS_MAX_POLLS = 75;
  const REWARD_TIMER_TICK_MS = 1000;
  const REWARD_CODE_PATTERN = /^NW15-[A-F0-9]{16}$/;
  const COOKIE_VISIBILITY_EVENT = 'nw:cookie-card-visibility';
  const SHARED_INTERRUPTION_KEY = 'nw-commerce-journey-interruption-v1';
  const TRACK_EVENT_NAME = 'NW Popup Interaction';
  const SCHEMA_VERSION = '1.0.0';
  const SOURCE_SYSTEM = 'nordweg_theme';
  const PENDING_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
  const SUBMIT_RECEIPT_FALLBACK_MS = 3000;
  const SCHEMA_PAGE_TYPES = new Set([
    'home', 'product', 'collection', 'cart', 'article', 'page', 'search', 'account', 'other',
  ]);
  const SCHEMA_STEP_NAMES = new Set([
    'teaser', 'email', 'sms', 'success', 'launcher', 'not_applicable',
  ]);
  const SCHEMA_CONSENT_MODES = new Set([
    'email_single_opt_in', 'email_double_opt_in', 'sms_double_opt_in',
    'email_then_sms', 'not_applicable', 'unknown',
  ]);
  const ALLOWED_ACTIONS = new Set([
    'eligible',
    'viewed',
    'opened',
    'step_viewed',
    'submitted',
    'accepted',
    'identified',
    'subscribed',
    'pending_double_opt_in',
    'duplicate_existing',
    'dismissed',
    'error',
  ]);
  const ALLOWED_PAGE_TYPES = ['index', 'product', 'collection'];
  const TRIGGER_PRIORITY = {
    [VISIT_TRIGGER]: 1,
  };

  class NordwegFirstOrderExitOffer extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;

      this.launcher = this.querySelector('[data-nw-exit-offer-launcher]');
      this.launcherKicker = this.querySelector('[data-nw-exit-offer-launcher-kicker]');
      this.launcherValue = this.querySelector('[data-nw-exit-offer-launcher-value]');
      this.launcherIcon = this.querySelector('[data-nw-exit-offer-launcher-icon]');
      this.launcherTab = this.querySelector('[data-nw-exit-offer-launcher-tab]');
      if (!this.launcher) return;

      this.bound = true;
      this.connectionEpoch = (this.connectionEpoch || 0) + 1;
      this.connectedAt = this.connectedAt || Date.now();
      this.pendingKlaviyoEvents = [];
      this.emittedStepSubmissions = new Set();
      this.emailAcceptanceRecorded = this.emailAcceptanceRecorded === true;
      this.emailEligibilityApproved = this.emailEligibilityApproved === true;
      this.emailEligibilityFailed = this.emailEligibilityFailed === true;
      this.emailEligibilityPending = false;
      this.emailIntent = this.emailIntent || null;
      this.emailProofPending = this.emailProofPending === true;
      this.smsEligibilityToken = this.smsEligibilityToken || '';
      this.smsEligibilityExpiresAt = this.smsEligibilityExpiresAt || '';
      this.smsEligibilityRemainingAtReceipt = Number(
        this.smsEligibilityRemainingAtReceipt || 0,
      );
      this.smsEligibilityObservedAt = Number(this.smsEligibilityObservedAt || 0);
      this.eligibilityEmailContext = this.eligibilityEmailContext || null;
      this.eligibilitySmsContext = this.eligibilitySmsContext || null;
      this.handoffClosing = false;
      this.pendingSmsFormOpen = false;
      this.sessionId = this.readOrCreateSessionId();
      this.journeyState = this.readJourneyState();
      this.authReturnContext = this.readEmailIdentityReturn();
      this.rewardState = this.readRewardState();
      this.smsExposureId = this.readStorage(this.dataset.smsExposureStorageKey, 'local');
      this.smsStatusPollCount = 0;
      if (this.smsExposureId) {
        // A resumed journey already belongs to a known Klaviyo profile, so its status
        // events must join back to the original exposure and must not queue behind an
        // identity gate that only a fresh form submission can open.
        this.exposureId = this.smsExposureId;
        this.identityReady = true;
        this.subscribedEmitted = this.readStorage(this.subscribedStorageKey(), 'local') === '1';
        this.smsStatusToken = this.readStorage(this.statusTokenStorageKey(), 'local');
        this.restoreSmsCaptureContext();
      }

      this.handleVisibilityChange = () => this.onVisibilityChange();
      this.handleVariantCommitted = (event) => this.onVariantCommitted(event);
      this.handleCookieVisibility = (event) => this.onCookieVisibilityChange(event);
      this.handleCheckoutClick = (event) => this.onCheckoutClick(event);
      this.handleCheckoutSubmit = (event) => this.onCheckoutSubmit(event);
      this.handleKlaviyoForm = (event) => this.onKlaviyoFormEvent(event);
      this.handleNativeKeydown = (event) => this.onNativeKeydown(event);
      this.handleNativeInput = (event) => this.onNativeInput(event);
      this.handleViewportChange = () => this.onViewportChange();
      this.handleSurfaceChange = () => this.scheduleSurfaceReconciliation();
      this.handlePageShow = (event) => {
        // connectedCallback already performs the initial reconciliation. Only a
        // bfcache restore needs pageshow to resume state, otherwise the first
        // request is immediately aborted and duplicated on every navigation.
        if (event?.persisted !== true) return;
        this.hardSuppressIfCheckoutStarted();
        if (!this.checkoutSuppressed) {
          this.expireSmsEligibilityIfNeeded();
          this.renderRewardCountdown();
          if (this.rewardState) void this.revalidateActiveReward();
          else this.resumePendingSmsStatus();
        }
      };
      this.handlePageHide = () => this.flushKlaviyoEvents();
      this.handleLauncher = () => this.onLauncherActivate();

      this.launcher.addEventListener('click', this.handleLauncher);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      document.addEventListener('nw:pdp-variant-committed', this.handleVariantCommitted);
      document.addEventListener(COOKIE_VISIBILITY_EVENT, this.handleCookieVisibility);
      document.addEventListener('click', this.handleCheckoutClick, true);
      document.addEventListener('submit', this.handleCheckoutSubmit, true);
      document.addEventListener('keydown', this.handleNativeKeydown, true);
      document.addEventListener('input', this.handleNativeInput, true);
      window.addEventListener('klaviyoForms', this.handleKlaviyoForm);
      window.addEventListener('resize', this.handleViewportChange);
      window.visualViewport?.addEventListener('resize', this.handleViewportChange);
      window.visualViewport?.addEventListener('scroll', this.handleViewportChange);
      window.addEventListener('pageshow', this.handlePageShow);
      window.addEventListener('pagehide', this.handlePageHide);
      document.addEventListener('visibilitychange', this.handlePageHide);
      document.addEventListener('toggle', this.handleSurfaceChange, true);
      document.addEventListener('focusin', this.handleSurfaceChange);
      document.addEventListener('focusout', this.handleSurfaceChange);
      if (typeof window.MutationObserver === 'function') {
        this.surfaceObserver = new window.MutationObserver(this.handleSurfaceChange);
        this.surfaceObserver.observe(document.documentElement, {
          subtree: true, childList: true, attributes: true,
          attributeFilter: ['open', 'closing', 'aria-hidden', 'hidden', 'class'],
        });
      }

      this.syncLauncherForStage(this.journeyState.stage);
      this.syncAttachedStackHeightSoon();

      if (this.checkoutStartedRecently()) {
        this.hardSuppressAfterCheckout();
        return;
      }

      // A server-issued reward outranks ordinary capture suppression. Its exact
      // expiry survives navigation and refresh; the browser never creates a new
      // 30-minute window.
      if (this.rewardState) {
        if (this.journeyState.stage !== 'reward') this.setJourneyStage('reward');
        this.showLauncher();
        this.startRewardCountdown();
        void this.revalidateActiveReward();
        return;
      }
      if (this.smsExposureId && this.smsStatusToken && this.dataset.smsStatusUrl) {
        // A signed status capability recovers the authoritative server state
        // after navigation. It never resubmits consent or creates a new timer.
        this.setJourneyStage('pending');
        this.showLauncher();
        void this.pollSmsStatus();
        return;
      }
      if (this.journeyState.stage === 'reward') {
        this.expireReward();
        return;
      }

      // A terminal or suppressed journey must be settled before any stage is resumed,
      // otherwise a customer who already received the credit keeps seeing the launcher.
      if (
        ['complete', 'reward'].includes(this.journeyState.stage)
        || this.wasCapturedRecently()
        || this.wasDismissedRecently()
      ) {
        this.clearEmailIdentityReturn();
        if (this.usesOfferTab()) this.showLauncher();
        else this.hideLauncher();
        return;
      }

      if (this.authReturnContext) {
        this.resumeEmailIdentityReturn();
        return;
      }

      if (['sms', 'pending'].includes(this.journeyState.stage)) {
        // The merchant switched the phone step off, or the confirmation window
        // expired. Never keep a launcher promising $15 through a path that has
        // stopped being able to confirm.
        if (
          !this.smsStepEnabled()
          || this.journeyStageExpired()
          || (this.journeyState.stage === 'sms' && !this.smsEligibilityCapabilityActive())
        ) {
          this.retireJourney();
          return;
        }
        this.showLauncher();
        if (this.journeyState.stage === 'sms') this.scheduleSmsEligibilityExpiry();
        if (this.journeyState.stage === 'pending') this.beginSmsStatusPolling();
        return;
      }

      if (this.journeyState.stage === 'qualifying') {
        if (!this.smsStepEnabled() || !this.transientEmail || this.journeyStageExpired()) {
          this.retireJourney();
          return;
        }
        this.showLauncher();
        void this.beginSmsEligibility('component_reconnected');
        return;
      }

      // Every page view counts toward the visit's second page, eligible or not.
      this.recordVisitPageView();
      // Once the block has been seen, the tab stays until sign-up.
      if (this.usesOfferTab()) this.showLauncher();
      this.scheduleVisitOpen();
    }

    // A runtime re-dock disconnects and reconnects the element. Re-measure so the
    // reserved stack height is re-established without waiting for a resize.
    syncAttachedStackHeightSoon() {
      window.requestAnimationFrame?.(() => {
        if (!this.bound) return;
        if (this.nativeFormOpen) this.refreshNativeLayout();
        else if (!this.hidden) this.syncAttachedStackHeight();
      });
    }

    disconnectedCallback() {
      if (!this.bound) return;
      this.connectionEpoch = (this.connectionEpoch || 0) + 1;
      this.removeNativeScrim();
      this.nativePressDialog?.removeEventListener?.('pointerdown', this.handleNativeDialogPress, true);
      this.nativePressDialog = null;

      this.launcher?.removeEventListener('click', this.handleLauncher);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      document.removeEventListener('nw:pdp-variant-committed', this.handleVariantCommitted);
      document.removeEventListener(COOKIE_VISIBILITY_EVENT, this.handleCookieVisibility);
      document.removeEventListener('click', this.handleCheckoutClick, true);
      document.removeEventListener('submit', this.handleCheckoutSubmit, true);
      document.removeEventListener('keydown', this.handleNativeKeydown, true);
      document.removeEventListener('input', this.handleNativeInput, true);
      window.removeEventListener('klaviyoForms', this.handleKlaviyoForm);
      window.removeEventListener('resize', this.handleViewportChange);
      window.visualViewport?.removeEventListener('resize', this.handleViewportChange);
      window.visualViewport?.removeEventListener('scroll', this.handleViewportChange);
      window.removeEventListener('pageshow', this.handlePageShow);
      window.removeEventListener('pagehide', this.handlePageHide);
      document.removeEventListener('visibilitychange', this.handlePageHide);
      document.removeEventListener('toggle', this.handleSurfaceChange, true);
      document.removeEventListener('focusin', this.handleSurfaceChange);
      document.removeEventListener('focusout', this.handleSurfaceChange);
      this.surfaceObserver?.disconnect();
      window.clearTimeout(this.surfaceTimer);

      window.clearTimeout(this.visitTimer);
      this.visitTimer = null;
      window.clearTimeout(this.formOpenTimer);
      window.clearTimeout(this.flushTimer);
      window.clearTimeout(this.smsStatusTimer);
      window.clearTimeout(this.rewardTimer);
      window.clearTimeout(this.submitReceiptTimer);
      window.clearTimeout(this.smsEligibilityTimer);
      window.clearTimeout(this.smsEligibilityRequestTimer);
      window.clearTimeout(this.smsHandoffTimer);
      this.smsStatusTimer = null;
      this.rewardTimer = null;
      this.smsEligibilityTimer = null;
      this.smsEligibilityRequestTimer = null;
      this.smsHandoffTimer = null;
      this.activeController?.abort();
      // The phone form has already submitted to Klaviyo at this point. Let the
      // idempotent keepalive handoff finish even if the element is re-docked or
      // the page starts navigating, otherwise consent could exist without its
      // matching reward state.
      if (!this.smsSubmissionPending) this.smsController?.abort();
      this.eligibilityController?.abort();
      this.smsEligibilityOperation = null;
      this.smsPollController?.abort();
      this.rewardStatusController?.abort();
      this.emailEligibilityPending = false;
      this.clearAttachedStackHeight();
      this.bound = false;
    }

    onVisibilityChange() {
      if (document.visibilityState === "visible" && this.emailProofPending) {
        void this.beginSmsEligibility("email_confirmation_return");
      }
      if (document.visibilityState === 'visible') this.expireSmsEligibilityIfNeeded();
      if (document.visibilityState === 'visible' && this.rewardState) {
        this.renderRewardCountdown();
        void this.revalidateActiveReward();
      }
      if (document.visibilityState === 'visible' && this.visitOpenPending) this.presentVisitOpen();
      if (document.visibilityState === 'visible') {
        // The ordinary polling schedule is intentionally bounded. Resume an
        // authoritative status check only when the shopper returns, without
        // resubmitting consent or creating a new credit/expiry window.
        this.resumePendingSmsStatus();
      }
    }

    resumePendingSmsStatus() {
      if (
        this.journeyState.stage !== 'pending'
        || !this.smsExposureId
        || !this.smsStatusToken
        || !this.dataset.smsStatusUrl
        || this.checkoutSuppressed
      ) return false;

      window.clearTimeout(this.smsStatusTimer);
      this.smsStatusTimer = null;
      this.smsStatusPollCount = 0;
      void this.pollSmsStatus();
      return true;
    }

    onVariantCommitted(event) {
      if (this.dataset.pageType !== 'product') return;
      this.dataset.currentProductOnSale = Number(event.detail?.compareAtPrice || 0)
        > Number(event.detail?.price || 0)
        ? 'true'
        : 'false';
    }

    onCookieVisibilityChange(event) {
      this.scheduleSurfaceReconciliation();
      if (event?.detail?.visible !== false || !this.deferredReason) return;
      if (document.visibilityState === 'hidden') return;
      if (this.authReturnContext) {
        this.deferredReason = null;
        this.resumeEmailIdentityReturn();
        return;
      }

      const reason = this.deferredReason;
      const triggerValue = this.deferredTriggerValue;
      this.deferredReason = null;
      this.deferredTriggerValue = '';
      this.tryPresent(reason, { triggerValue });
    }

    onViewportChange() {
      this.syncAttachedStackHeight();
      this.refreshNativeLayout();
      // The purchase dock can reparent the launcher after the resize event.
      // Reconcile against the settled layout without opening another form.
      window.requestAnimationFrame?.(() => {
        if (this.bound && this.nativeFormOpen) this.refreshNativeLayout();
      });
      if (!this.activeFormId) return;
      if (this.journeyState.stage !== 'email') return;

      const expectedFormId = this.resolveFormContext().formId;
      if (expectedFormId !== this.activeFormId && !this.nativeFormOpen) {
        this.activeFormId = '';
        this.activeFormVersionId = '';
        this.activeDesignId = '';
      }
    }

    onCheckoutClick(event) {
      const target = event.target?.closest?.(
        'button[name="checkout"], a[href*="/checkout"], .nw-checkout-action',
      );
      if (target) this.rememberCheckoutStarted();
    }

    onCheckoutSubmit(event) {
      if (event.submitter?.name === 'checkout') this.rememberCheckoutStarted();
    }

    async tryPresent(reason, { triggerValue = '' } = {}) {
      if (
        this.pendingEligibility
        || this.nativeOpenRequested
        || this.nativeFormOpen
        || this.nativeFormUnavailable
        || document.visibilityState === 'hidden'
        || this.checkoutSuppressed
        || this.checkoutStartedRecently()
        || document.documentElement.classList.contains('shopify-design-mode')
        || !this.isEligiblePage()
      ) return false;

      if (
        this.journeyState.stage === 'complete'
        || this.wasCapturedRecently()
        || this.wasDismissedRecently()
        || this.wasShownThisSession()
      ) return false;

      if (this.hasBlockingSurface()) {
        this.deferUntilCookieResolved(reason, triggerValue);
        return false;
      }

      this.pendingEligibility = true;
      const connectionEpoch = this.connectionEpoch || 0;

      try {
        const cart = await this.getCart();
        if (!this.isEligibleCart(cart)) return false;
        if ((this.connectionEpoch || 0) !== connectionEpoch
          || document.visibilityState === 'hidden' || this.checkoutSuppressed
          || this.checkoutStartedRecently() || !this.isEligiblePage()
          || this.journeyState.stage === 'complete' || this.wasCapturedRecently()
          || this.wasDismissedRecently() || this.wasShownThisSession()) return false;
        // Cart reads are asynchronous: a shopper may open navigation, type in
        // another form, or enter the bag before this response arrives.
        if (this.hasBlockingSurface()) {
          this.deferUntilCookieResolved(reason, triggerValue);
          return false;
        }
        if (!this.claimJourneySlot(reason)) return false;

        const formContext = this.resolveFormContext();
        this.currentTrigger = reason;
        this.currentTriggerValue = triggerValue;
        this.exposureId = this.uuid();
        this.setActiveFormContext(formContext);
        this.emit('eligible', {
          source_event: 'eligibility_passed',
          trigger: reason,
          trigger_value: triggerValue,
          step_name: this.journeyState.stage,
        });
        this.openNativeForm({ reason, deliberate: false });
        return true;
      } catch (error) {
        this.emit('error', {
          source_event: 'eligibility_error',
          trigger: reason,
          trigger_value: triggerValue,
          error_code: 'cart_read_failed',
        });
        return false;
      } finally {
        this.pendingEligibility = false;
      }
    }

    // Rules 2 and 3: once the block has been closed, or seen during this visit, the
    // tab stays on every offer page until sign-up.
    usesOfferTab() {
      return this.journeyState.stage === 'email'
        && !this.emailIdentityReturnReady
        && this.isEligiblePage()
        // A form that failed to load also leaves the tab, so a visitor can retry.
        && (this.hasDismissed() || this.wasShownThisSession() || this.nativeFormUnavailable)
        && !this.wasCapturedRecently()
        && !this.checkoutStartedRecently();
    }

    // Rule 1: the block opens once per visit, on the second page view or after 30 s
    // on the site, whichever comes first.
    scheduleVisitOpen() {
      window.clearTimeout(this.visitTimer);
      this.visitTimer = null;
      if (!this.isEligiblePage() || this.wasShownThisSession() || this.wasDismissedRecently()
        || this.wasCapturedRecently()) return;
      const session = this.recordVisitPageView();
      const secondPage = session.pageViews >= 2;
      this.visitTriggerValue = secondPage ? 'second_page_view' : `dwell_${VISIT_DELAY_MS}ms`;
      const delay = secondPage ? 0 : Math.max(0, VISIT_DELAY_MS - (Date.now() - session.startedAt));
      this.visitTimer = window.setTimeout(() => {
        this.visitTimer = null;
        this.presentVisitOpen();
      }, delay);
    }

    presentVisitOpen() {
      // A background tab opens the block when it is first seen, not while hidden.
      this.visitOpenPending = document.visibilityState === 'hidden';
      if (this.visitOpenPending) return;
      void this.tryPresent(VISIT_TRIGGER, { triggerValue: this.visitTriggerValue || '' });
    }

    recordVisitPageView() {
      let session = {};
      try {
        session = JSON.parse(this.readStorage(VISIT_SESSION_KEY, 'session') || '{}') || {};
      } catch (error) {
        session = {};
      }
      const now = Date.now();
      const startedAt = Number.isSafeInteger(session.startedAt) && session.startedAt <= now
        ? session.startedAt : now;
      // A runtime re-dock reconnects the element; it is still the same page view.
      const pageViews = (Number.isSafeInteger(session.pageViews) ? session.pageViews : 0)
        + (this.visitPageViewCounted ? 0 : 1);
      this.visitPageViewCounted = true;
      this.writeStorage(VISIT_SESSION_KEY, JSON.stringify({ startedAt, pageViews }), 'session');
      return { startedAt, pageViews };
    }

    onLauncherActivate() {
      if (this.emailIdentityRequired) {
        this.startEmailIdentitySignIn();
        return;
      }
      if (this.emailIdentityReturnReady) {
        if (!this.readEmailIdentityReturn() || !this.smsStepEnabled()
          || this.dataset.smsCampaignEligible !== 'true' || this.isCookieCardVisible()) {
          this.clearEmailIdentityReturn();
          this.hideLauncher();
          return;
        }
        // Returning from sign-in is not a provider submission or consent proof.
        // Only a deliberate click reopens the existing native email form.
        if (this.openNativeForm({reason: this.currentTrigger || 'launcher', deliberate: true})) {
          this.clearEmailIdentityReturn();
        }
        return;
      }
      if (this.emailProofPending) {
        void this.beginSmsEligibility("email_confirmation_retry");
        return;
      }
      if (this.journeyState.stage === 'reward') {
        this.applyReward();
        return;
      }
      if (this.journeyState.stage === 'qualifying') return;
      if (this.journeyState.stage === 'pending') {
        // The native form already accepted the phone. This is a status action,
        // never a second opt-in or a reason to hide the confirmation rail.
        if (!this.emailConfirmationRequired) this.resumePendingSmsStatus();
        return;
      }
      if (this.emailConfirmationRequired) {
        // The rail is a live status surface while email DOI is outstanding.
        // Reopening the form would create a second submission without proving
        // the inbox confirmation the reward contract requires.
        return;
      }
      // The launcher is the only control the theme owns. While Klaviyo's form is
      // open it must close it, not ask Klaviyo to open it a second time.
      if (this.nativeFormOpen) {
        this.closeNativeForm();
        return;
      }
      if (this.usesOfferTab()) this.currentTrigger = OFFER_TAB_TRIGGER;
      this.openNativeForm({
        reason: this.currentTrigger || this.dataset.triggerReason || 'launcher',
        deliberate: true,
      });
    }

    readEmailIdentityReturn() {
      const raw = this.readStorage(EMAIL_AUTH_RETURN_KEY, 'session');
      if (!raw) return null;
      try {
        const marker = JSON.parse(raw);
        const age = Date.now() - marker.createdAt;
        if (marker.version !== this.dataset.journeyVersion
          || marker.sessionId !== this.sessionId
          || !Number.isSafeInteger(marker.createdAt) || age < 0 || age > EMAIL_AUTH_RETURN_MAX_MS
          || !/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(marker.exposureId)
          || typeof marker.campaign !== 'boolean'
          || !['email', 'qualifying'].includes(this.journeyState.stage)
          || !this.wasShownThisSession()) throw new Error('INVALID_AUTH_RETURN');
        return marker;
      } catch (error) {
        this.clearEmailIdentityReturn();
        return null;
      }
    }

    clearEmailIdentityReturn() {
      try { window.sessionStorage.removeItem(EMAIL_AUTH_RETURN_KEY); } catch (error) { /* Fail closed. */ }
      this.authReturnContext = null;
      this.emailIdentityReturnReady = false;
    }

    startEmailIdentitySignIn() {
      if (!this.smsStepEnabled() || this.dataset.smsCampaignEligible !== 'true'
        || !this.wasShownThisSession() || this.wasCapturedRecently() || this.wasDismissedRecently()
        || this.checkoutSuppressed || this.checkoutStartedRecently()
        || this.journeyState.stage !== 'qualifying') return false;
      try {
        const previous = this.readEmailIdentityReturn();
        if (!previous && !this.emailAcceptanceRecorded) return false;
        const current = new URL(window.location.href);
        const login = new URL(this.dataset.accountLoginUrl, current);
        if (login.origin !== current.origin || login.pathname !== '/customer_authentication/login') return false;
        // Never copy email, identity capabilities, arbitrary query data or a
        // caller-supplied return URL into Shopify authentication/navigation.
        const destination = new URL(current.pathname, current.origin);
        const campaign = previous?.campaign ?? this.isSmsCampaignIntent();
        if (campaign) destination.searchParams.set('nw_offer', 'sms15');
        const variant = current.searchParams.get('variant');
        if (/^[0-9]{1,20}$/.test(variant || '') && this.dataset.pageType === 'product') {
          destination.searchParams.set('variant', variant);
        }
        login.searchParams.delete('login_hint');
        login.searchParams.delete('return_url');
        login.searchParams.set('return_to', destination.pathname + destination.search);
        const marker = previous || {
          version: this.dataset.journeyVersion, sessionId: this.sessionId,
          createdAt: Date.now(), exposureId: this.exposureId, campaign,
        };
        // Storage must succeed before leaving the page. This is a short-lived
        // UI continuation only, not contact data, consent or reward authority.
        const encoded = JSON.stringify(marker);
        window.sessionStorage.setItem(EMAIL_AUTH_RETURN_KEY, encoded);
        if (window.sessionStorage.getItem(EMAIL_AUTH_RETURN_KEY) !== encoded) return false;
        this.authReturnContext = marker;
        window.location.assign(login.pathname + login.search);
        return true;
      } catch (error) {
        return false;
      }
    }

    resumeEmailIdentityReturn() {
      const marker = this.readEmailIdentityReturn();
      if (!marker || !this.smsStepEnabled() || this.dataset.smsCampaignEligible !== 'true'
        || this.checkoutSuppressed || this.checkoutStartedRecently()
        || this.wasCapturedRecently() || this.wasDismissedRecently()
        || !ALLOWED_PAGE_TYPES.includes(this.dataset.pageType)
        || document.documentElement.classList.contains('shopify-design-mode')
        || !this.claimJourneySlot('sms15-campaign')) {
        this.clearEmailIdentityReturn();
        this.hideLauncher();
        return false;
      }
      if (this.isCookieCardVisible()) {
        this.deferUntilCookieResolved('sms15-campaign', '');
        this.hideLauncher();
        return false;
      }
      this.authReturnContext = marker;
      this.clearSmsEligibility();
      this.emailAcceptanceRecorded = false;
      this.emailEligibilityFailed = false;
      this.emailIntent = null;
      this.transientEmail = '';
      this.transientPhone = '';
      this.exposureId = marker.exposureId;
      this.currentTrigger = marker.campaign ? 'sms15-campaign' : 'launcher';
      this.setActiveFormContext(this.resolveFormContext('', 'email'));
      if (this.dataset.customerAuthenticated === 'true') {
        this.emailIdentityReturnReady = true;
        this.setJourneyStage('email');
      } else {
        // A canceled or unsuccessful login must not claim identity proof.
        this.emailProofPending = true;
        this.emailIdentityRequired = true;
        this.setJourneyStage('qualifying');
      }
      this.showLauncher();
      return true;
    }

    // The block's phone field shows a plain "+1" before the number. AutoFill can
    // fill a number that already starts with +1; the prefix then steps aside so
    // the code is not shown twice. Klaviyo's value and submission are untouched.
    onNativeInput(event) {
      const input = event?.target;
      if (!input?.matches?.('form[data-nw-native-block] .nw-native-phone input')) return;
      input.parentElement?.toggleAttribute?.('data-nw-has-country-code', /^\s*\+/.test(String(input.value || '')));
    }

    onNativeKeydown(event) {
      if (event.key !== 'Escape' || event.isComposing || !this.nativeFormOpen
        || this.closingForSurface || this.hasBlockingSurface()) return;
      // Use the native close control so Klaviyo owns the close receipt and the
      // existing dismissal rules. Keyboard dismissal returns to the launcher. The
      // placed block leaves at once, as it does for its X (Owner, 2026-10-25).
      const previouslyHadFocus = this.launcherHadFocus;
      this.launcherHadFocus = true;
      const dialog = this.nativeDialog;
      if (dialog?.hasAttribute?.('data-nw-native-active') === true) {
        this.beginNativeExit(dialog);
        if (dialog.hasAttribute?.('data-nw-native-closing') !== true) {
          this.launcherHadFocus = previouslyHadFocus;
          return;
        }
      } else if (!this.closeNativeForm()) {
        this.launcherHadFocus = previouslyHadFocus;
        return;
      }
      event.preventDefault();
      event.stopPropagation();
    }

    closeNativeForm() {
      const formId = this.activeFormId || this.resolveFormContext().formId;
      if (!/^[A-Za-z0-9_-]+$/.test(String(formId || ''))) return false;
      // openForm is public; closeForm is not. Activate Klaviyo's actual close
      // control within this exact native form instead of queuing a made-up API.
      // Selector read back from the real native renderer on 2026-09-06.
      const form = document.querySelector(`[data-testid="klaviyo-form-${formId}"]`);
      const close = form?.closest?.('[role="dialog"]')
        ?.querySelector?.('button.klaviyo-close-form[aria-label="Close dialog"]');
      if (!close || typeof close.click !== 'function') return false;
      close.click();
      return true;
    }

    openNativeForm({ reason = 'launcher', deliberate = false } = {}) {
      if (this.checkoutSuppressed || this.checkoutStartedRecently()) return false;
      // A finished journey must never re-offer what the shopper already claimed.
      if (['complete', 'reward'].includes(this.journeyState.stage) || this.wasCapturedRecently()) {
        this.hideLauncher();
        return false;
      }
      if (this.nativeOpenRequested || this.nativeFormOpen) return false;
      if (this.hasBlockingSurface()) {
        this.deferredNativeOpen = {reason, deliberate};
        this.syncSurfaceState();
        return false;
      }
      if (this.journeyState.stage === 'sms' && !this.smsEligibilityCapabilityActive()) {
        this.emit('error', {
          source_event: 'open_form',
          trigger: reason,
          step_name: 'sms',
          error_code: 'sms_eligibility_expired',
        });
        this.retireJourney();
        return false;
      }

      const formContext = this.journeyState.stage === 'sms'
        ? this.eligibilitySmsContext
        : this.resolveFormContext();
      if (!formContext?.formId) {
        this.emit('error', {
          source_event: 'open_form',
          trigger: reason,
          error_code: 'missing_form_id',
        });
        return false;
      }

      this.currentTrigger = reason;
      this.currentTriggerValue = this.currentTriggerValue || '';
      this.exposureId = this.exposureId || this.uuid();
      this.setActiveFormContext(formContext);
      // While the block is open its own heading and × stand in for the tab.
      this.hideLauncher();

      if (deliberate) {
        this.emit('opened', {
          source_event: 'launcher_click',
          trigger: reason,
          step_name: this.journeyState.stage,
        });
      }

      window._klOnsite = window._klOnsite || [];
      this.nativeOpenRequested = true;
      window._klOnsite.push(['openForm', formContext.formId]);
      window.clearTimeout(this.formOpenTimer);
      this.formOpenTimer = window.setTimeout(() => {
        if (this.nativeFormOpen) return;
        this.nativeOpenRequested = false;
        // The native form did not open. Keep the visible launcher so a visitor
        // can retry after a transient load failure; do not claim a form view.
        this.nativeFormUnavailable = true;
        this.setLauncherExpanded(false);
        this.showLauncher();
        this.emit('error', {
          source_event: 'open_form',
          trigger: reason,
          step_name: this.journeyState.stage,
          error_code: 'native_form_open_timeout',
        });
      }, FORM_OPEN_TIMEOUT_MS);
      return true;
    }

    onKlaviyoFormEvent(event) {
      const detail = event?.detail || {};
      const formId = String(detail.formId || detail.formID || '');
      const eventType = String(detail.type || '');
      if (!this.isOwnedForm(formId)) {
        // Inline acquisition belongs to its own native report. Its submission
        // suppresses another email popup, without asserting confirmed consent.
        if (eventType === 'submit' && this.journeyState.stage === 'email'
          && /^[A-Za-z0-9]+$/.test(formId)
          && document.querySelector(`[data-nw-marketing-form][data-nw-form-id="${formId}"][data-nw-delivery-mode="klaviyo_native"]`)) {
          this.deferredReason = null;
          this.deferredNativeOpen = null;
          this.nativeOpenRequested = false;
          window.clearTimeout(this.formOpenTimer);
          this.setJourneyStage('complete');
          this.rememberCapture();
          if (this.nativeFormOpen) this.closeNativeForm();
          this.hideLauncher();
        }
        return;
      }

      const formContext = this.resolveFormContext(formId);
      const isSmsForm = formContext.channel === 'sms';

      // Native receipts can arrive after handoff, navigation, or completion.
      // Never let the previous email screen replace the accepted SMS context.
      if (['submit', 'stepSubmit'].includes(eventType)) {
        if (this.checkoutSuppressed || this.journeyState.stage === 'complete') return;
        if (!isSmsForm && this.emailAcceptanceRecorded) return;
        if (isSmsForm && ['pending', 'reward'].includes(this.journeyState.stage)) return;
        if (isSmsForm && formId !== this.eligibilitySmsContext?.formId) {
          this.emit('error', {
            source_event: 'klaviyoForms.' + eventType,
            step_name: 'sms',
            error_code: 'sms_form_binding_mismatch',
          });
          return;
        }
      }
      if (!isSmsForm && eventType === 'close' && this.smsHandoffCompleted) return;
      this.setActiveFormContext(formContext);

      if (eventType === 'open') {
        window.clearTimeout(this.formOpenTimer);
        this.nativeOpenRequested = false;
        this.nativeFormUnavailable = false;
        this.nativeFormOpen = true;
        this.rememberShown();
        this.launcherHadFocus = document.activeElement === this.launcher;
        this.hideLauncher();
        this.syncNativeAnchor();
        this.emit('viewed', {
          source_event: 'klaviyoForms.open',
          trigger: this.currentTrigger || 'unknown',
          step_name: formContext.channel,
        });
        this.emitStepViewed(formContext.channel, 'klaviyoForms.open');
        this.syncSurfaceState();
        if (isSmsForm) this.scheduleSmsEligibilityExpiry();
        return;
      }

      if (eventType === 'close') {
        this.clearNativeAnchor(formId);
        if (
          !isSmsForm
          && formId === this.emailHandoffFormId
          && (this.handoffClosing || this.smsHandoffCompleted)
        ) {
          if (this.handoffClosing) this.finishSmsFormHandoff();
          else if (this.eligibilitySmsContext) {
            this.setActiveFormContext(this.eligibilitySmsContext);
          }
          return;
        }
        window.clearTimeout(this.formOpenTimer);
        this.nativeOpenRequested = false;
        this.nativeFormOpen = false;
        this.setLauncherExpanded(false);
        if (this.closingForSurface) {
          this.closingForSurface = false;
          this.launcherHadFocus = false;
          if (this.journeyState.stage === 'complete') this.hideLauncher();
          else this.showLauncher();
          return;
        }
        // Closing the confirmation screen of a capture that already succeeded is
        // how the form is meant to end. It is not a dismissal, it must not be
        // counted as one, and the offer must not come back.
        if (this.journeyState.stage === 'reward' && this.rewardIsActive()) {
          this.showLauncher();
          this.restoreLauncherFocus();
          return;
        }
        if (this.journeyState.stage === 'complete' || this.wasCapturedRecently()) {
          this.hideLauncher();
          return;
        }
        // Owner, 2026-10-24: a close never opens another form, on any device. It is
        // recorded first so the quiet tab takes the launcher's place.
        if (!isSmsForm && this.journeyState.stage === 'email') this.rememberDismissal();
        this.showLauncher();
        this.restoreLauncherFocus();
        this.emit('dismissed', {
          source_event: 'klaviyoForms.close',
          trigger: this.currentTrigger || 'unknown',
          step_name: formContext.channel,
        });
        return;
      }

      if (eventType === 'submit') {
        this.rememberTransientIdentity(detail.metaData);
        this.identityReady = true;
        this.markIdentified('klaviyoForms.submit');
        if (isSmsForm) {
          // Klaviyo defines submit as the one main conversion receipt, including
          // a phone-only form. stepSubmit normally carries the same phone data,
          // but the consent bridge must still work if only submit is observed.
          void this.handleStepSubmission(
            formContext,
            detail.metaData,
            'klaviyoForms.submit',
          );
          this.scheduleKlaviyoFlush();
          return;
        }
        // Klaviyo's form-level submit is the acceptance receipt. A step submission
        // on its own is not, so acceptance is never asserted from it.
        window.clearTimeout(this.submitReceiptTimer);
        this.submitReceiptTimer = null;
        this.recordEmailAcceptance('klaviyoForms.submit');
        if (!this.smsStepEnabled() || this.emailEligibilityFailed) {
          this.completeEmailOnlyCapture('klaviyoForms.submit');
        } else {
          void this.beginSmsEligibility('klaviyoForms.submit');
          this.maybeAdvanceToSmsForm();
        }
        this.scheduleKlaviyoFlush();
        return;
      }

      if (eventType === 'stepSubmit') {
        this.rememberTransientIdentity(detail.metaData);
        this.identityReady = true;
        this.markIdentified('klaviyoForms.stepSubmit');
        void this.handleStepSubmission(formContext, detail.metaData);
        this.scheduleKlaviyoFlush();
        return;
      }

      // Klaviyo owns destination clicks. They are not another form-open event.
    }

    async handleStepSubmission(
      formContext,
      metaData = {},
      sourceEvent = 'klaviyoForms.stepSubmit',
    ) {
      // The phone step already reached Shopify. A later step submission must never
      // roll the journey back to 'sms' or resubmit consent for the same person.
      if (['pending', 'reward', 'complete'].includes(this.journeyState.stage)) return;

      const isSmsStep = formContext.channel === 'sms';
      if (!isSmsStep && this.emailAcceptanceRecorded) return;
      const canonicalStep = isSmsStep ? 'sms' : 'email';
      if (!this.emittedStepSubmissions.has(canonicalStep)) {
        this.emittedStepSubmissions.add(canonicalStep);
        this.emit('submitted', {
          source_event: sourceEvent,
          trigger: this.currentTrigger || 'unknown',
          step_name: canonicalStep,
          consent_mode: isSmsStep ? 'sms_double_opt_in' : 'email_single_opt_in',
        });
      }

      if (isSmsStep) {
        await this.submitSmsConsent(metaData);
        return;
      }

      if (!this.smsStepEnabled()) {
        // Owner, 2026-10-24: the email step is the sign-up. The block's phone step
        // comes after it and Klaviyo owns it, so a shopper who closes there keeps
        // WELCOME10 in the bag and never sees the tab or the block again.
        this.recordEmailAcceptance(sourceEvent);
        this.completeEmailOnlyCapture(sourceEvent);
        return;
      }

      this.setJourneyStage('qualifying');
      // stepSubmit proves that Klaviyo accepted this individual step, but the
      // documented form-level submit event is the single conversion receipt.
      // Wait for that receipt before sending email to the eligibility service.
      window.clearTimeout(this.submitReceiptTimer);
      this.submitReceiptTimer = window.setTimeout(() => {
        this.submitReceiptTimer = null;
        if (this.emailAcceptanceRecorded) return;
        this.emit('error', {
          source_event: 'klaviyoForms.stepSubmit',
          step_name: 'email',
          error_code: 'email_submit_receipt_missing',
        });
        this.retireJourney();
      }, SUBMIT_RECEIPT_FALLBACK_MS);
    }

    recordEmailAcceptance(sourceEvent = 'klaviyoForms.submit') {
      if (this.emailAcceptanceRecorded) return;
      // Klaviyo fires submit and stepSubmit for the same action and does not
      // guarantee their order, so submitted is emitted here if it has not
      // already been. The funnel never reads accepted before submitted.
      if (!this.emittedStepSubmissions.has('email')) {
        this.emittedStepSubmissions.add('email');
        this.emit('submitted', {
          source_event: sourceEvent,
          step_name: 'email',
          consent_mode: 'email_single_opt_in',
        });
      }
      // Klaviyo accepted the submission. Email is single opt-in in Klaviyo (owner,
      // 2026-10-24); Klaviyo still owns the subscribed state.
      this.emit('accepted', {
        source_event: sourceEvent,
        step_name: 'email',
        consent_mode: 'email_single_opt_in',
      });
      this.emit('pending_double_opt_in', {
        source_event: sourceEvent,
        step_name: 'email',
        consent_mode: 'email_single_opt_in',
      });
      this.emailAcceptanceRecorded = true;
    }

    completeEmailOnlyCapture(sourceEvent = 'klaviyoForms.submit') {
      if (this.emailOnlyCaptureCompleted || !this.emailAcceptanceRecorded) return;
      this.emailOnlyCaptureCompleted = true;
      this.recordEmailAcceptance(sourceEvent);
      this.transientEmail = '';
      this.transientPhone = '';
      this.emailIntent = null;
      this.emailProofPending = false;
      this.clearSmsEligibility();
      this.setJourneyStage('complete');
      this.rememberCapture();
      this.hideLauncher();
      void this.applyWelcomeDiscount();
    }

    // Owner, 2026-10-24: after sign-up the welcome code waits in the cart, so it is
    // already applied at checkout. Shopify's /discount route sets it without leaving
    // the page.
    applyWelcomeDiscount() {
      const code = String(this.dataset.welcomeDiscountCode || '').trim();
      if (!code || this.welcomeDiscountRequested || typeof fetch !== 'function') return Promise.resolve(false);
      this.welcomeDiscountRequested = true;
      const root = window.Shopify?.routes?.root || '/';
      const url = `${root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(`${root}cart.js`)}`;
      return fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then((response) => {
          if (!response.ok) throw new Error(`Discount request failed with status ${response.status}.`);
          document.dispatchEvent(new CustomEvent('nw:welcome-discount-applied', { detail: { code } }));
          return true;
        })
        .catch(() => {
          this.welcomeDiscountRequested = false;
          return false;
        });
    }

    monotonicNow() {
      const candidate = window.performance?.now?.();
      return Number.isFinite(candidate) ? Number(candidate) : Date.now();
    }

    setSmsEligibilityCapability(result) {
      const eligibilityToken = String(result?.eligibilityToken || '').trim();
      const eligibilityExpiresAt = String(result?.eligibilityExpiresAt || '').trim();
      const serverNow = String(result?.serverNow || '').trim();
      const expiresAtMs = Date.parse(eligibilityExpiresAt);
      const serverNowMs = Date.parse(serverNow);
      const remainingMs = expiresAtMs - serverNowMs;
      if (
        !/^[A-Za-z0-9_-]{32,256}$/.test(eligibilityToken)
        || !Number.isFinite(expiresAtMs)
        || !Number.isFinite(serverNowMs)
        || remainingMs <= 0
        || remainingMs > SMS_ELIGIBILITY_MAX_MS
      ) return false;

      this.smsEligibilityToken = eligibilityToken;
      this.smsEligibilityExpiresAt = eligibilityExpiresAt;
      this.smsEligibilityRemainingAtReceipt = remainingMs;
      this.smsEligibilityObservedAt = this.monotonicNow();
      this.emailEligibilityApproved = true;
      this.scheduleSmsEligibilityExpiry();
      return true;
    }

    smsEligibilityRemainingMs() {
      if (!this.smsEligibilityToken || !this.smsEligibilityRemainingAtReceipt) return 0;
      const elapsed = Math.max(0, this.monotonicNow() - this.smsEligibilityObservedAt);
      return Math.max(0, this.smsEligibilityRemainingAtReceipt - elapsed);
    }

    smsEligibilityCapabilityActive() {
      return this.smsEligibilityRemainingMs() > 0;
    }

    scheduleSmsEligibilityExpiry() {
      window.clearTimeout(this.smsEligibilityTimer);
      this.smsEligibilityTimer = null;
      const remainingMs = this.smsEligibilityRemainingMs();
      if (remainingMs <= 0) {
        this.expireSmsEligibilityIfNeeded();
        return;
      }
      this.smsEligibilityTimer = window.setTimeout(() => {
        this.smsEligibilityTimer = null;
        this.expireSmsEligibilityIfNeeded();
      }, Math.ceil(remainingMs) + 10);
    }

    expireSmsEligibilityIfNeeded() {
      if (!this.smsEligibilityToken || this.smsEligibilityCapabilityActive()) return false;
      this.emit('error', {
        source_event: 'sms_eligibility',
        step_name: 'sms',
        error_code: 'sms_eligibility_expired',
      });
      if (this.nativeFormOpen && this.activeFormChannel === 'sms') this.closeNativeForm();
      this.retireJourney();
      return true;
    }

    clearSmsEligibility() {
      window.clearTimeout(this.smsEligibilityTimer);
      window.clearTimeout(this.smsEligibilityRequestTimer);
      window.clearTimeout(this.smsHandoffTimer);
      this.smsEligibilityTimer = null;
      this.smsEligibilityRequestTimer = null;
      this.smsHandoffTimer = null;
      this.eligibilityController?.abort();
      this.smsEligibilityOperation = null;
      this.eligibilityController = null;
      this.emailEligibilityPending = false;
      this.emailEligibilityApproved = false;
      this.emailIdentityRequired = false;
      this.emailProofPending = false;
      this.smsEligibilityToken = '';
      this.smsEligibilityExpiresAt = '';
      this.smsEligibilityRemainingAtReceipt = 0;
      this.smsEligibilityObservedAt = 0;
      this.eligibilityEmailContext = null;
      this.eligibilitySmsContext = null;
      this.pendingSmsFormOpen = false;
      this.handoffClosing = false;
    }

    failSmsEligibility(reasonCode, sourceEvent = 'sms_eligibility') {
      if (this.emailEligibilityFailed) return false;
      this.emailEligibilityFailed = true;
      this.emailEligibilityApproved = false;
      this.smsEligibilityToken = '';
      this.smsEligibilityExpiresAt = '';
      this.smsEligibilityRemainingAtReceipt = 0;
      this.emit('error', {
        source_event: sourceEvent,
        step_name: 'email',
        consent_mode: 'email_single_opt_in',
        error_code: String(reasonCode || 'sms_eligibility_unavailable')
          .toLocaleLowerCase('en-US'),
      });
      if (this.emailAcceptanceRecorded) this.completeEmailOnlyCapture(sourceEvent);
      return false;
    }

    async ensureEmailIntent(payload, operation) {
      if (this.emailIntent) return this.emailIntent;
      if (!this.dataset.smsIntentUrl) throw new Error('EMAIL_INTENT_UNAVAILABLE');
      const response = await this.requestSms(this.dataset.smsIntentUrl, {
        method: 'POST', credentials: 'same-origin',
        headers: {Accept: 'application/json', 'Content-Type': 'application/json'},
        body: JSON.stringify(payload),
      }, 'eligibilityController');
      const result = await response.json();
      if (this.smsEligibilityOperation !== operation || this.checkoutSuppressed
        || this.journeyState.stage === 'complete') {
        const error = new Error('EMAIL_INTENT_CANCELLED');
        error.name = 'AbortError';
        throw error;
      }
      if (!response.ok || result.accepted !== true
        || result.exposureId !== this.exposureId
        || !Number.isSafeInteger(result.emailIntentIssuedAt)
        || !/^[A-Za-z0-9_-]{32,128}$/.test(String(result.emailIntentToken || ''))) {
        throw new Error('EMAIL_INTENT_UNAVAILABLE');
      }
      this.emailIntent = {
        emailIntentIssuedAt: result.emailIntentIssuedAt,
        emailIntentToken: result.emailIntentToken,
      };
      return this.emailIntent;
    }

    async beginSmsEligibility(sourceEvent = 'klaviyoForms.submit') {
      if (
        this.emailEligibilityPending
        || this.emailEligibilityApproved
        || this.emailEligibilityFailed
      ) return false;
      if (!this.emailAcceptanceRecorded) return false;
      if (!this.smsStepEnabled() || !this.dataset.smsEligibilityUrl) {
        return this.failSmsEligibility('sms_step_disabled', sourceEvent);
      }
      if (!this.transientEmail) {
        return this.failSmsEligibility('missing_transient_email', sourceEvent);
      }

      const activeContext = this.activeFormId
        ? this.resolveFormContext(this.activeFormId, 'email')
        : this.resolveFormContext('', 'email');
      const smsContext = this.resolveFormContext(activeContext.formId, 'sms');
      if (!activeContext.formId || !smsContext.formId) {
        return this.failSmsEligibility('missing_form_id', sourceEvent);
      }

      this.eligibilityEmailContext = activeContext;
      this.eligibilitySmsContext = smsContext;
      this.exposureId = this.exposureId || this.uuid();
      this.emailEligibilityPending = true;
      this.setJourneyStage('qualifying');
      const operation = {deadline: this.monotonicNow() + SMS_ELIGIBILITY_TIMEOUT_MS};
      this.smsEligibilityOperation = operation;
      const operationActive = () => this.smsEligibilityOperation === operation
        && !this.checkoutSuppressed && this.journeyState.stage !== 'complete';
      const assertWithinDeadline = () => {
        if (this.monotonicNow() >= operation.deadline) {
          const error = new Error('SMS_ELIGIBILITY_REQUEST_TIMEOUT');
          error.name = 'AbortError';
          throw error;
        }
      };

      try {
        window.clearTimeout(this.smsEligibilityRequestTimer);
        this.smsEligibilityRequestTimer = window.setTimeout(() => {
          if (!operationActive()) return;
          operation.deadline = 0;
          this.eligibilityController?.abort();
        }, SMS_ELIGIBILITY_TIMEOUT_MS);
        const requestOptions = {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: this.transientEmail,
            popupId: this.dataset.popupId || '',
            journeyVersion: this.dataset.journeyVersion || '',
            emailDesignId: activeContext.designId || '',
            emailFormId: activeContext.formId || '',
            smsDesignId: smsContext.designId || '',
            smsFormId: smsContext.formId || '',
            offerId: smsContext.offerId || '',
            deliveryMode: 'klaviyo_native',
            exposureId: this.exposureId,
          }),
        };
        const eligibilityPayload = JSON.parse(requestOptions.body);
        const emailIntent = await this.ensureEmailIntent(eligibilityPayload, operation);
        if (!operationActive()) return false;
        assertWithinDeadline();
        requestOptions.body = JSON.stringify({...eligibilityPayload, ...emailIntent});
        const retryDelays = [0, 350, 900, 1800];
        let response;
        let result = {};
        for (let attempt = 0; attempt < retryDelays.length; attempt += 1) {
          if (retryDelays[attempt] > 0) {
            await this.wait(retryDelays[attempt]);
          }
          if (!operationActive()) return false;
          assertWithinDeadline();
          response = await this.requestSms(
            this.dataset.smsEligibilityUrl,
            requestOptions,
            'eligibilityController',
          );
          try {
            result = await response.json();
          } catch (error) {
            result = {reasonCode: 'sms_eligibility_invalid_response'};
          }
          if (!operationActive()) return false;
          assertWithinDeadline();
          const retryReason = String(result.reasonCode || '').toLocaleLowerCase('en-US');
          if (
            response.ok
            || retryReason !== 'email_profile_not_ready'
            || attempt === retryDelays.length - 1
          ) break;
        }
        if (!response) throw new Error('SMS_ELIGIBILITY_REQUEST_NOT_SENT');
        if (['EMAIL_DOUBLE_OPT_IN_REQUIRED', 'EMAIL_IDENTITY_CONFIRMATION_REQUIRED']
          .includes(result.reasonCode)) {
          // Retain the same signed intent only in memory. Returning from the
          // inbox checks proof, never renews its start time or re-submits email.
          this.emailProofPending = true;
          this.emailIdentityRequired = result.reasonCode === 'EMAIL_IDENTITY_CONFIRMATION_REQUIRED';
          this.setJourneyStage('qualifying');
          this.showLauncher();
          return false;
        }
        this.emailProofPending = false;
        this.emailIdentityRequired = false;
        if (!response.ok || result.eligible !== true) {
          return this.failSmsEligibility(
            result.reasonCode || 'sms_eligibility_unavailable',
            'shopify_sms_eligibility',
          );
        }
        if (!this.setSmsEligibilityCapability(result)) {
          return this.failSmsEligibility(
            'sms_eligibility_invalid_capability',
            'shopify_sms_eligibility',
          );
        }
        this.maybeAdvanceToSmsForm();
        return true;
      } catch (error) {
        if (!operationActive()) return false;
        return this.failSmsEligibility(
          error?.name === 'AbortError'
            ? 'sms_eligibility_request_timeout'
            : 'sms_eligibility_request_failed',
          'shopify_sms_eligibility',
        );
      } finally {
        if (this.smsEligibilityOperation === operation) {
          window.clearTimeout(this.smsEligibilityRequestTimer);
          this.smsEligibilityRequestTimer = null;
          this.smsEligibilityOperation = null;
          this.emailEligibilityPending = false;
        }
      }
    }

    maybeAdvanceToSmsForm() {
      if (
        !this.emailAcceptanceRecorded
        || !this.emailEligibilityApproved
        || this.pendingSmsFormOpen
        || this.smsHandoffCompleted
      ) return false;
      if (!this.smsEligibilityCapabilityActive()) {
        return this.failSmsEligibility('sms_eligibility_expired', 'shopify_sms_eligibility');
      }

      this.pendingSmsFormOpen = true;
      this.emailHandoffFormId = this.eligibilityEmailContext?.formId || '';
      this.setJourneyStage('sms');
      if (this.nativeFormOpen && this.activeFormChannel === 'email') {
        this.handoffClosing = true;
        window.clearTimeout(this.smsHandoffTimer);
        this.smsHandoffTimer = window.setTimeout(
          () => this.failSmsEligibility('native_form_close_timeout', 'email_handoff'),
          FORM_OPEN_TIMEOUT_MS,
        );
        if (!this.closeNativeForm()) {
          return this.failSmsEligibility('native_form_close_unavailable', 'email_handoff');
        }
      } else {
        this.finishSmsFormHandoff();
      }
      return true;
    }

    finishSmsFormHandoff() {
      if (!this.pendingSmsFormOpen || this.smsHandoffCompleted) return false;
      window.clearTimeout(this.smsHandoffTimer);
      this.smsHandoffTimer = null;
      this.handoffClosing = false;
      this.pendingSmsFormOpen = false;
      if (!this.smsEligibilityCapabilityActive()) {
        this.expireSmsEligibilityIfNeeded();
        return false;
      }

      this.smsHandoffCompleted = true;
      this.nativeFormOpen = false;
      this.setLauncherExpanded(false);
      if (this.eligibilitySmsContext) this.setActiveFormContext(this.eligibilitySmsContext);
      return this.openNativeForm({reason: 'email-qualified', deliberate: false});
    }

    restoreLauncherFocus() {
      if (!this.launcherHadFocus) return;
      this.launcherHadFocus = false;
      if (!this.launcher || this.launcher.hidden) return;
      const restore = () => {
        if (this.nativeFormOpen || this.launcher?.hidden || this.isConnected === false
          || this.hasBlockingSurface()) return;
        try { this.launcher.focus(); } catch (_error) { /* Keep the native journey usable. */ }
      };
      restore();
      // Native unmount can restore its previous focus after the close receipt.
      // Settle after that frame, while yielding if another surface has opened.
      window.requestAnimationFrame?.(() => window.requestAnimationFrame?.(restore));
    }

    refreshNativeLayout() {
      if (!this.nativeFormOpen) return;
      if (!this.hidden || !this.launcher?.hidden) this.hideLauncher();
      this.syncAttachedStackHeight();
      this.syncNativeAnchor();
    }

    syncNativeAnchor() {
      if (!this.nativeFormOpen || !this.activeFormId) {
        this.removeNativeScrim();
        return;
      }
      const form = document.querySelector(`[data-testid="klaviyo-form-${this.activeFormId}"]`);
      const dialog = form?.closest?.('[role="dialog"]');
      if (!dialog?.setAttribute) {
        this.removeNativeScrim();
        return;
      }
      this.nativeDialog = dialog;
      this.nativeDialogFormId = this.activeFormId;
      if (!dialog.hasAttribute?.('data-nw-native-active')) dialog.setAttribute('data-nw-native-active', '');
      // The block's styles key on this marker, not on a form id, so a new Klaviyo
      // copy of the block keeps them. Klaviyo may re-render the form between steps;
      // the surface observer brings the marker back.
      if (this.activeFormChannel !== 'sms' && !form.hasAttribute?.('data-nw-native-block')) {
        form.setAttribute?.('data-nw-native-block', '');
      }
      // Native Klaviyo renders a scaled width wrapper inside its dialog. Keep
      // those exact ancestors unscaled while the block is placed by the theme.
      // Do not depend on generated class names or alter any field/validation.
      for (let frame = form.parentElement; frame && frame !== dialog; frame = frame.parentElement) {
        if (!frame.hasAttribute?.('data-nw-native-frame')) frame.setAttribute('data-nw-native-frame', '');
      }
      // Owner, 2026-10-24: one block. Desktop shows a centred pop-up (2026-10-25); phones
      // show a floating card. The purchase bar hides while the block is open, so the
      // card rests on the bottom of the visible area. Fixed positions are measured
      // against the layout viewport; the keyboard only shrinks the visual one, and on
      // iOS innerHeight follows the visual viewport, so neither is clamped to it.
      const viewport = window.visualViewport;
      const layoutHeight = document.documentElement?.clientHeight || window.innerHeight;
      const visibleTop = Math.max(0, viewport?.offsetTop || 0);
      const visibleBottom = viewport ? viewport.offsetTop + viewport.height : layoutHeight;
      const compact = window.matchMedia(NATIVE_PHONE_QUERY).matches;
      // The pop-up's look keys on this marker, so the card and the pop-up never mix.
      if (compact) dialog.removeAttribute?.('data-nw-native-modal');
      else if (!dialog.hasAttribute?.('data-nw-native-modal')) dialog.setAttribute('data-nw-native-modal', '');
      // While a field in the block has focus on a phone, the card rides 8px above
      // Safari's address pill over the keyboard, drops its photo and its line, and
      // scrolls inside, never the page.
      const focused = document.activeElement;
      const typing = compact && form.contains?.(focused) === true
        && /^(INPUT|TEXTAREA|SELECT)$/.test(focused?.tagName || '');
      // Owner, 2026-10-24: the block floats on every device. Nothing sits flush with
      // the bottom edge, where iOS Safari would tint its toolbar with the block.
      const gap = compact ? (typing ? 8 : 12) : 24;
      const inset = compact ? 12 : 24;
      const anchorTop = Math.min(visibleBottom, layoutHeight) - gap;
      const typingChanged = typing !== (dialog.hasAttribute?.('data-nw-native-typing') === true);
      const startRect = typingChanged ? dialog.getBoundingClientRect?.() : null;
      if (typingChanged) dialog.toggleAttribute?.('data-nw-native-typing', typing);
      this.syncNativeFieldHints(form);
      const viewportWidth = viewport?.width || window.innerWidth || MODAL_WIDTH;
      dialog.style.setProperty('--nw-native-inline-start', `${(viewport?.offsetLeft || 0) + inset}px`);
      dialog.style.setProperty('--nw-native-width', `${compact
        ? viewportWidth - 2 * inset : Math.min(MODAL_WIDTH, viewportWidth - 2 * inset)}px`);
      dialog.style.setProperty('--nw-native-visible-top', `${visibleTop}px`);
      const anchorBottom = Math.max(0, layoutHeight - anchorTop);
      dialog.style.setProperty('--nw-native-anchor-bottom', `${anchorBottom}px`);
      // Owner, 2026-10-24: on phones the sheet takes about 45% of the screen at most and
      // scrolls inside; the close control stays outside the scroll. The share is of the
      // whole screen, not Safari's smaller page area, so a 375x667 iPhone still fits the
      // email step. While typing it may use the whole visible area above the keyboard.
      const screenHeight = Math.max(layoutHeight, Number(window.screen?.height) || 0);
      const heightCap = compact && !typing ? Math.round(screenHeight * PHONE_SHEET_SHARE) : Infinity;
      // The pop-up keeps 24px clear above and below; past that it scrolls inside.
      const maxHeight = typing
        ? visibleBottom - visibleTop - 2 * gap
        : compact ? Math.min(anchorTop - visibleTop - 12, heightCap) : visibleBottom - visibleTop - 2 * gap;
      dialog.style.setProperty('--nw-native-max-height', `${Math.max(0, maxHeight)}px`);
      this.fitNativeStrip(dialog, form, compact && !typing ? maxHeight : Infinity);
      // Resting on the bottom of the screen, a phone card also clears the home
      // indicator; above the keyboard it does not need to.
      dialog.style.setProperty('--nw-native-safe-bottom',
        compact && !typing && anchorBottom === gap ? 'env(safe-area-inset-bottom, 0px)' : '0px');
      if (typing) {
        const height = dialog.getBoundingClientRect?.().height || 0;
        dialog.style.setProperty('--nw-native-top',
          `${Math.round(Math.max(visibleTop + gap, visibleBottom - gap - height))}px`);
        this.keepFocusedRowVisible(form, focused);
      } else {
        dialog.style.removeProperty?.('--nw-native-top');
      }
      // The card moves with the keyboard instead of jumping to its new place.
      if (startRect && typeof dialog.animate === 'function'
        && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const shift = startRect.top - (dialog.getBoundingClientRect?.().top ?? startRect.top);
        if (Math.abs(shift) > 1) {
          dialog.animate([{ translate: `0 ${Math.round(shift)}px` }, { translate: '0 0' }],
            { duration: 220, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' });
        }
      }
      this.syncNativeCloseHeader(dialog, form);
      this.syncNativeScrim(dialog, form);
    }

    // Owner, 2026-10-24 (Igor): a tap anywhere on the dimmed page closes the block
    // the way its X does, and never reaches what is underneath. A clear layer sits
    // just below the card; it has no colour, so Safari never tints its bars with it.
    syncNativeScrim(dialog, form) {
      if (form?.hasAttribute?.('data-nw-native-block') !== true) {
        this.removeNativeScrim();
        return;
      }
      // The X and KEEP SHOPPING are Klaviyo's and re-rendered between steps, so one
      // listener on the dialog catches them as the finger lands.
      if (this.nativePressDialog !== dialog) {
        if (!this.handleNativeDialogPress) this.handleNativeDialogPress = (event) => this.onNativeDialogPress(event);
        this.nativePressDialog?.removeEventListener?.('pointerdown', this.handleNativeDialogPress, true);
        dialog.addEventListener?.('pointerdown', this.handleNativeDialogPress, true);
        this.nativePressDialog = dialog;
      }
      let scrim = this.nativeScrim;
      if (!scrim) {
        scrim = document.createElement?.('div');
        if (!scrim?.setAttribute) return;
        scrim.setAttribute('data-nw-native-scrim', '');
        scrim.setAttribute('aria-hidden', 'true');
        // Owner, 2026-10-24 (Igor): a tap outside acts the moment the finger lands, not
        // on the click iOS sends later. The listeners leave with the layer.
        scrim.addEventListener('pointerdown', (event) => this.onNativeScrimPress(event));
        scrim.addEventListener('click', (event) => this.onNativeScrimClick(event, scrim));
        const hold = (event) => { if (event.cancelable !== false) event.preventDefault?.(); };
        scrim.addEventListener('touchmove', hold, { passive: false });
        scrim.addEventListener('wheel', hold, { passive: false });
        this.nativeScrim = scrim;
      }
      const layer = Number.parseInt(window.getComputedStyle?.(dialog)?.zIndex, 10);
      const zIndex = String(Number.isFinite(layer) ? layer - 1 : 89999);
      if (scrim.style && scrim.style.zIndex !== zIndex) scrim.style.zIndex = zIndex;
      if (scrim.parentNode !== document.body) document.body?.appendChild?.(scrim);
    }

    // With the keyboard up, the first tap outside only puts the keyboard away, so a
    // stray tap never loses what was typed. Otherwise the block leaves at once: the
    // card fades and the page clears in the same frame, without waiting for Klaviyo.
    // Klaviyo's own X closes it underneath, so the close receipt, the 10% tab and the
    // quiet period are the same as for the X.
    onNativeScrimPress(event) {
      if (event?.isPrimary === false || (event?.button ?? 0) !== 0) return;
      const dialog = this.nativeDialog;
      if (dialog?.hasAttribute?.('data-nw-native-typing') === true) {
        const focused = document.activeElement;
        if (focused && dialog.contains?.(focused)) focused.blur?.();
        return;
      }
      this.beginNativeExit(dialog);
    }

    // Owner, 2026-10-24 (Igor): the X and KEEP SHOPPING close as fast as a tap outside.
    // They act as the finger lands too; a field in the block puts its keyboard away.
    onNativeDialogPress(event) {
      if (event?.isPrimary === false || (event?.button ?? 0) !== 0) return;
      const dialog = this.nativeDialog;
      // Klaviyo puts a button block's class on the button itself.
      const control = event?.target?.closest?.('button.klaviyo-close-form, button.nw-native-continue, .nw-native-continue button');
      if (!control || !dialog?.contains?.(control)) return;
      const focused = document.activeElement;
      if (focused && dialog.contains?.(focused)) focused.blur?.();
      if (dialog.hasAttribute?.('data-nw-native-closing') === true) return;
      this.beginNativeExit(dialog);
      if (dialog.hasAttribute?.('data-nw-native-closing') !== true) return;
      // The block is already closing; the click this press ends in must not close it
      // a second time.
      const swallow = (click) => {
        click.preventDefault?.();
        click.stopPropagation?.();
      };
      dialog.addEventListener?.('click', swallow, { capture: true, once: true });
      window.setTimeout?.(() => dialog.removeEventListener?.('click', swallow, { capture: true }), NATIVE_SCRIM_LINGER_MS);
    }

    // The block leaves in the frame it is asked to: the card fades and the page clears
    // without waiting for Klaviyo, whose own X then closes it underneath.
    beginNativeExit(dialog) {
      if (dialog?.hasAttribute?.('data-nw-native-closing') === true) return;
      const scrim = this.nativeScrim;
      if (!this.nativeFormOpen || this.closingForSurface || !dialog) {
        this.removeNativeScrim();
        return;
      }
      dialog.setAttribute?.('data-nw-native-closing', '');
      // The layer stays a moment, still clear, so this tap's click lands on it and not
      // on the page that was underneath (the card no longer takes taps once closing).
      this.nativeScrim = null;
      window.setTimeout?.(() => scrim?.remove?.(), NATIVE_SCRIM_LINGER_MS);
      if (!this.closeNativeForm()) {
        // Never leave the page covered, or the block half gone, if it can't close.
        dialog.removeAttribute?.('data-nw-native-closing');
        scrim?.remove?.();
      }
    }

    // The click that follows a tap never reaches the page. A layer that already let
    // the block go leaves with it.
    onNativeScrimClick(event, scrim) {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      if (scrim && scrim !== this.nativeScrim) scrim.remove?.();
    }

    removeNativeScrim() {
      if (!this.nativeScrim) return;
      this.nativeScrim.remove?.();
      this.nativeScrim = null;
    }

    // Owner, 2026-10-24: iPhone fills the right thing in the keyboard's bar. The
    // phone row already shows +1, so it asks for the national number. Klaviyo's
    // default country stays the United States.
    syncNativeFieldHints(form) {
      const hints = [['.nw-native-email input', 'autocomplete', 'email'],
        ['.nw-native-phone input', 'autocomplete', 'tel-national'], ['.nw-native-phone input', 'inputmode', 'tel']];
      for (const [selector, name, value] of hints) {
        const field = form?.querySelector?.(selector);
        if (field && field.getAttribute?.(name) !== value) field.setAttribute?.(name, value);
      }
    }

    // Owner, 2026-10-24: the strip is 120px on phones. Where the email step would
    // outgrow the sheet (a 375x667 iPhone), the strip gives up the difference, down
    // to 80px, so the legal line stays in view without scrolling.
    fitNativeStrip(dialog, form, maxHeight) {
      const shown = form?.querySelector?.('.nw-strip img')?.getBoundingClientRect?.().height || 0;
      let height = PHONE_STRIP_HEIGHT;
      if (shown > 0 && Number.isFinite(maxHeight)) {
        // The form's own border comes out of the sheet's height too.
        const room = maxHeight - Math.max(0, (form.offsetHeight || 0) - (form.clientHeight || 0));
        const natural = (form.scrollHeight || 0) - shown + PHONE_STRIP_HEIGHT;
        height = Math.max(PHONE_STRIP_MIN_HEIGHT,
          Math.min(PHONE_STRIP_HEIGHT, Math.floor(PHONE_STRIP_HEIGHT - (natural - room))));
      }
      if (height < PHONE_STRIP_HEIGHT) dialog.style.setProperty('--nw-native-strip-height', `${height}px`);
      else dialog.style.removeProperty?.('--nw-native-strip-height');
    }

    // The focused field and its button stay in view inside the card's own scroll.
    keepFocusedRowVisible(form, focused) {
      const row = focused?.closest?.('[data-testid="form-row"]');
      if (!row?.getBoundingClientRect || !form?.getBoundingClientRect) return;
      const frame = form.getBoundingClientRect();
      const box = row.getBoundingClientRect();
      if (box.bottom > frame.bottom) form.scrollTop += box.bottom - frame.bottom + 8;
      else if (box.top < frame.top) form.scrollTop -= frame.top - box.top + 8;
    }

    syncNativeCloseHeader(dialog, form) {
      const close = dialog.querySelector?.('button.klaviyo-close-form');
      let frame = close?.parentElement;
      while (frame && frame !== dialog && !frame.contains?.(form)) frame = frame.parentElement;
      const hasHeader = Boolean(frame && close && window.getComputedStyle?.(close)?.display !== 'none');
      const header = hasHeader ? frame : null;
      dialog.querySelectorAll?.('[data-nw-native-close-frame]').forEach(previous => {
        if (previous !== header) previous.removeAttribute('data-nw-native-close-frame');
      });
      if (dialog !== header) dialog.removeAttribute('data-nw-native-close-frame');
      if (header && !header.hasAttribute?.('data-nw-native-close-frame')) {
        header.setAttribute('data-nw-native-close-frame', '');
      }
      // The × sits on the first line of text (an eyebrow or the heading), top right,
      // below any photo strip.
      const title = form?.querySelector?.('.nw-native-eyebrow, .nw-native-title');
      const heading = title?.querySelector?.('h1, h2, h3, p') || title;
      if (header?.getBoundingClientRect && heading?.getBoundingClientRect) {
        const style = window.getComputedStyle?.(heading);
        const lineHeight = Number.parseFloat(style?.lineHeight)
          || (Number.parseFloat(style?.fontSize) || 26) * 1.15;
        const top = heading.getBoundingClientRect().top - header.getBoundingClientRect().top;
        dialog.style.setProperty('--nw-native-close-top',
          `${Math.max(0, Math.round(top + lineHeight / 2 - 22))}px`);
      } else {
        dialog.style.removeProperty?.('--nw-native-close-top');
      }
    }

    clearNativeAnchor(formId) {
      const form = document.querySelector(`[data-testid="klaviyo-form-${formId}"]`);
      const dialog = form?.closest?.('[role="dialog"]')
        || (this.nativeDialogFormId === formId ? this.nativeDialog : null);
      if (!dialog) return;
      for (const attribute of ['data-nw-native-active', 'data-nw-native-close-frame', 'data-nw-native-typing',
        'data-nw-native-closing', 'data-nw-native-modal']) {
        dialog.removeAttribute?.(attribute);
      }
      form?.removeAttribute?.('data-nw-native-block');
      dialog.querySelectorAll?.('[data-nw-native-frame], [data-nw-native-close-frame]').forEach(frame => {
        frame.removeAttribute('data-nw-native-frame');
        frame.removeAttribute('data-nw-native-close-frame');
      });
      for (const property of ['--nw-native-anchor-bottom', '--nw-native-visible-top', '--nw-native-max-height',
        '--nw-native-inline-start', '--nw-native-width', '--nw-native-close-top', '--nw-native-safe-bottom',
        '--nw-native-top', '--nw-native-strip-height']) {
        dialog.style?.removeProperty?.(property);
      }
      if (this.nativeDialog === dialog) {
        this.removeNativeScrim();
        this.nativeDialog = null;
        this.nativeDialogFormId = '';
      }
    }

    retireJourney() {
      window.clearTimeout(this.smsStatusTimer);
      window.clearTimeout(this.rewardTimer);
      this.smsStatusTimer = null;
      this.rewardTimer = null;
      this.clearSmsEligibility();
      this.clearSmsExposure();
      this.transientEmail = '';
      this.transientPhone = '';
      this.emailIntent = null;
      this.emailProofPending = false;
      this.setJourneyStage('complete');
      this.rememberCapture();
      this.hideLauncher();
    }

    journeyStageExpired() {
      const updatedAt = Number(this.journeyState?.updatedAt || 0);
      return updatedAt > 0 && Date.now() - updatedAt > PENDING_MAX_AGE_MS;
    }

    subscribedStorageKey() {
      const key = this.dataset.smsExposureStorageKey;
      return key ? key + '-subscribed' : '';
    }

    // exposure_id is broadcast into the dataLayer, so it cannot authorise a read
    // of anyone's consent state. The consent response mints this token instead and
    // it is deliberately never emitted as an event property.
    statusTokenStorageKey() {
      const key = this.dataset.smsExposureStorageKey;
      return key ? key + '-status-token' : '';
    }

    rewardIssuedStorageKey() {
      const key = this.dataset.smsRewardStorageKey;
      return key ? key + '-issued-event' : '';
    }

    normalizeReward(code, expiresAt) {
      const normalizedCode = String(code || '').trim().toLocaleUpperCase('en-US');
      const expiresAtMs = Date.parse(String(expiresAt || ''));
      if (!REWARD_CODE_PATTERN.test(normalizedCode) || !Number.isFinite(expiresAtMs)) return null;
      return {code: normalizedCode, expiresAt: new Date(expiresAtMs).toISOString()};
    }

    readRewardState() {
      const key = this.dataset.smsRewardStorageKey;
      if (!key) return null;
      try {
        const stored = JSON.parse(this.readStorage(key, 'local') || 'null');
        const reward = this.normalizeReward(stored?.code, stored?.expiresAt);
        const localRemainingMs = reward
          ? Date.parse(reward.expiresAt) - Date.now()
          : 0;
        const observedRemainingMs = Number(stored?.observedRemainingMs);
        const observedAtMs = Number(stored?.observedAtMs);
        const observedElapsedMs = Number.isFinite(observedAtMs)
          ? Math.max(0, Date.now() - observedAtMs)
          : 0;
        const boundedRemainingMs = (
          Number.isFinite(observedRemainingMs)
          && observedRemainingMs >= 0
          && Number.isFinite(observedAtMs)
        )
          ? Math.min(localRemainingMs, observedRemainingMs - observedElapsedMs)
          : localRemainingMs;
        if (!reward || boundedRemainingMs <= 0) {
          window.localStorage.removeItem(key);
          return null;
        }
        if (boundedRemainingMs < localRemainingMs) {
          this.anchorRewardClock(new Date(
            Date.parse(reward.expiresAt) - boundedRemainingMs,
          ).toISOString());
        }
        return reward;
      } catch (error) {
        try {
          window.localStorage.removeItem(key);
        } catch (storageError) {
          // A malformed local hint can be ignored; Shopify still owns validity.
        }
        return null;
      }
    }

    clearRewardState() {
      this.rewardState = null;
      this.rewardClock = null;
      window.clearTimeout(this.rewardTimer);
      this.rewardTimer = null;
      try {
        if (this.dataset.smsRewardStorageKey) {
          window.localStorage.removeItem(this.dataset.smsRewardStorageKey);
        }
        const issuedKey = this.rewardIssuedStorageKey();
        if (issuedKey) window.localStorage.removeItem(issuedKey);
      } catch (error) {
        // Shopify's discount endsAt remains authoritative if storage is unavailable.
      }
    }

    rewardRemainingMs() {
      if (!this.rewardState) return 0;
      return Math.max(0, Date.parse(this.rewardState.expiresAt) - this.rewardNowMs());
    }

    persistRewardState() {
      const key = this.dataset.smsRewardStorageKey;
      if (!key || !this.rewardState) return;
      this.writeStorage(key, JSON.stringify({
        code: this.rewardState.code,
        expiresAt: this.rewardState.expiresAt,
        observedRemainingMs: this.rewardRemainingMs(),
        observedAtMs: Date.now(),
      }), 'local');
    }

    anchorRewardClock(serverNow) {
      const serverNowMs = Date.parse(String(serverNow || ''));
      const monotonicMs = Number(window.performance?.now?.());
      if (!Number.isFinite(serverNowMs) || !Number.isFinite(monotonicMs)) return false;
      this.rewardClock = {serverNowMs, monotonicMs};
      return true;
    }

    rewardNowMs() {
      const monotonicMs = Number(window.performance?.now?.());
      if (this.rewardClock && Number.isFinite(monotonicMs)) {
        return this.rewardClock.serverNowMs
          + Math.max(0, monotonicMs - this.rewardClock.monotonicMs);
      }
      return Date.now();
    }

    rewardIsActive() {
      return Boolean(this.rewardState && this.rewardRemainingMs() > 0);
    }

    rewardCountdownLabel() {
      const totalSeconds = Math.max(0, Math.ceil(this.rewardRemainingMs() / 1000));
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = String(totalSeconds % 60).padStart(2, '0');
      const prefix = String(this.dataset.smsRewardLauncherValuePrefix || 'USE •').trim();
      return `${prefix} ${minutes}:${seconds}`;
    }

    startRewardCountdown() {
      window.clearTimeout(this.rewardTimer);
      this.rewardTimer = null;
      if (!this.renderRewardCountdown()) return;
      this.rewardTimer = window.setTimeout(() => {
        this.rewardTimer = null;
        this.startRewardCountdown();
      }, REWARD_TIMER_TICK_MS);
    }

    renderRewardCountdown() {
      if (!this.rewardState) return false;
      if (!this.rewardIsActive()) {
        this.expireReward();
        return false;
      }
      // Persist a non-increasing remaining-time observation. On a later reload,
      // a rolled-back wall clock cannot make the rail count upward while the
      // signed authoritative status read is offline or still pending.
      this.persistRewardState();
      this.syncLauncherForStage('reward');
      return true;
    }

    activateReward(code, expiresAt, serverNow = '') {
      if (this.checkoutSuppressed || this.checkoutStartedRecently()) return false;
      this.emailConfirmationRequired = false;
      const reward = this.normalizeReward(code, expiresAt);
      const priorRemaining = this.rewardState?.code === reward?.code
        ? this.rewardRemainingMs() : null;
      if (serverNow) this.anchorRewardClock(serverNow);
      if (!reward || Date.parse(reward.expiresAt) <= this.rewardNowMs()) {
        this.expireReward();
        return false;
      }
      this.rewardState = reward;
      if (priorRemaining !== null && this.rewardRemainingMs() > priorRemaining) {
        this.anchorRewardClock(new Date(Date.parse(reward.expiresAt) - priorRemaining).toISOString());
      }
      this.persistRewardState();
      this.setJourneyStage('reward');
      this.rememberCapture();
      if (this.nativeFormOpen) this.closeNativeForm();
      this.showLauncher();
      this.startRewardCountdown();

      const issuedKey = this.rewardIssuedStorageKey();
      if (!this.readStorage(issuedKey, 'local')) {
        this.emitCommercial('NW Offer Issued', {
          offer_id: this.dataset.smsOfferId || '',
          popup_id: this.dataset.popupId || '',
          popup_version: this.dataset.smsPopupVersion || '',
          klaviyo_form_id: this.activeFormId || '',
          design_id: this.activeDesignId || '',
          exposure_id: this.smsExposureId || this.exposureId || '',
          amount: '15.00',
          currency: 'USD',
          duration_seconds: 1800,
        });
        this.writeStorage(issuedKey, '1', 'local');
      }
      this.flushKlaviyoEvents();
      return true;
    }

    async revalidateActiveReward() {
      if (!this.rewardState) return false;
      if (!this.smsExposureId || !this.dataset.smsStatusUrl || !this.smsStatusToken) {
        // Local storage is only continuity state. Without the signed capability,
        // it cannot prove that the customer-bound Shopify discount is active.
        this.expireReward();
        return false;
      }

      try {
        const response = await this.requestSms(
          this.dataset.smsStatusUrl,
          this.smsStatusRequestOptions(),
          'rewardStatusController',
        );
        const terminalHttpStatus = [401, 403, 404, 410].includes(Number(response.status));
        let result = {};
        try {
          result = await response.json();
        } catch (error) {
          if (terminalHttpStatus) this.expireReward();
          return false;
        }
        if (
          response.ok
          && result.rewardActive === true
          && result.rewardCode
          && result.rewardExpiresAt
        ) {
          return this.activateReward(
            result.rewardCode,
            result.rewardExpiresAt,
            result.serverNow,
          );
        }
        if (
          (response.ok && (
            result.rewardExpired === true
            || ['FAILED', 'SUPPRESSED'].includes(String(result.creditStatus || ''))
          ))
          || terminalHttpStatus
        ) {
          this.expireReward();
        }
      } catch (error) {
        // A temporary network failure cannot extend the Shopify discount. Keep
        // the last server expiry and reconcile again on the next visible page.
      }
      return false;
    }

    expireReward() {
      this.clearRewardState();
      this.clearSmsExposure();
      this.setJourneyStage('complete');
      this.rememberCapture();
      this.hideLauncher();
    }

    applyReward() {
      if (!this.rewardIsActive()) {
        this.expireReward();
        return false;
      }
      const location = window.location;
      const redirect = `${location.pathname || '/'}${location.search || ''}${location.hash || ''}`;
      const url = `/discount/${encodeURIComponent('WELCOME10,' + this.rewardState.code)}`
        + `?redirect=${encodeURIComponent(redirect)}`;
      if (typeof location.assign === 'function') location.assign(url);
      else location.href = url;
      return true;
    }

    recordSmsSubscribed(sourceEvent) {
      if (this.subscribedEmitted) return false;
      this.subscribedEmitted = true;
      this.writeStorage(this.subscribedStorageKey(), '1', 'local');
      this.emit('subscribed', {
        source_event: sourceEvent,
        step_name: 'sms',
        consent_mode: 'sms_double_opt_in',
      });
      return true;
    }

    markIdentified(sourceEvent) {
      if (this.identityEmitted || (!this.transientEmail && !this.transientPhone)) return;
      this.identityEmitted = true;
      this.emit('identified', {
        source_event: sourceEvent,
        trigger: this.currentTrigger || 'unknown',
        step_name: this.journeyState.stage,
      });
    }

    rememberTransientIdentity(metaData = {}) {
      const email = String(
        metaData.$email || metaData.email || metaData.email_address || '',
      ).trim();
      const phone = String(
        metaData.$phone_number || metaData.phone_number || metaData.phone || '',
      ).trim();
      if (email) this.transientEmail = email;
      if (phone) this.transientPhone = phone;
    }

    async submitSmsConsent(metaData = {}) {
      if (this.smsSubmissionPending) return false;
      if (!this.smsStepEnabled() || !this.dataset.smsConsentUrl) {
        this.emit('error', {
          source_event: 'shopify_sms_consent',
          step_name: 'sms',
          error_code: 'sms_step_disabled',
        });
        this.retireJourney();
        return false;
      }
      if (!this.smsEligibilityCapabilityActive()) {
        this.emit('error', {
          source_event: 'shopify_sms_consent',
          step_name: 'sms',
          consent_mode: 'sms_double_opt_in',
          error_code: 'sms_eligibility_required',
        });
        this.retireJourney();
        return false;
      }
      this.rememberTransientIdentity(metaData);
      if (!this.transientEmail || !this.transientPhone) {
        this.emit('error', {
          source_event: 'shopify_sms_consent',
          step_name: 'sms',
          consent_mode: 'sms_double_opt_in',
          error_code: 'missing_transient_contact',
        });
        this.retireJourney();
        return false;
      }

      const context = this.eligibilitySmsContext
        || this.resolveFormContext(this.activeFormId, 'sms');
      const emailContext = this.eligibilityEmailContext
        || this.resolveFormContext(context.formId, 'email');
      const exposureId = this.exposureId || this.uuid();
      this.exposureId = exposureId;
      this.smsSubmissionPending = true;
      try {
        const requestOptions = {
          method: 'POST',
          credentials: 'same-origin',
          keepalive: true,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: this.transientEmail,
            phone: this.transientPhone,
            disclosureAccepted: true,
            popupId: this.dataset.popupId || '',
            popupVersion: context.popupVersion || '',
            designId: context.designId || '',
            klaviyoFormId: context.formId || '',
            offerId: context.offerId || '',
            deliveryMode: 'klaviyo_native',
            exposureId,
            smsListId: this.dataset.smsListId || '',
            journeyVersion: this.dataset.journeyVersion || '',
            emailDesignId: emailContext.designId || '',
            emailFormId: emailContext.formId || '',
            eligibilityExpiresAt: this.smsEligibilityExpiresAt,
            eligibilityToken: this.smsEligibilityToken,
            ...this.emailIntent,
          }),
        };
        const retryDelays = [0, 500, 1500, 3000];
        let response;
        let result = {};
        for (let attempt = 0; attempt < retryDelays.length; attempt += 1) {
          if (retryDelays[attempt] > 0) {
            await this.wait(retryDelays[attempt]);
          }
          response = await this.requestSms(
            this.dataset.smsConsentUrl,
            requestOptions,
          );
          try {
            result = await response.json();
          } catch (error) {
            result = {reasonCode: 'sms_consent_invalid_response'};
          }
          const retryReason = String(result.reasonCode || '').toLocaleLowerCase('en-US');
          if (
            response.ok
            || retryReason !== 'email_profile_not_ready'
            || attempt === retryDelays.length - 1
          ) break;
        }
        if (!response) throw new Error('SMS_CONSENT_REQUEST_NOT_SENT');
        const reasonCode = String(result.reasonCode || 'sms_consent_rejected')
          .toLocaleLowerCase('en-US');
        if (!response.ok || result.accepted !== true) {
          if (['duplicate_existing', 'sms_discount_already_issued'].includes(reasonCode)) {
            this.emit('duplicate_existing', {
              source_event: 'shopify_sms_consent',
              step_name: 'sms',
              consent_mode: 'sms_double_opt_in',
            });
            this.retireJourney();
            return false;
          }
          this.emit('error', {
            source_event: 'shopify_sms_consent',
            step_name: 'sms',
            consent_mode: 'sms_double_opt_in',
            error_code: reasonCode,
          });
          this.retireJourney();
          return false;
        }

        this.smsExposureId = this.exposureId;
        this.writeStorage(
          this.dataset.smsExposureStorageKey,
          this.smsExposureId,
          'local',
        );
        this.smsStatusToken = String(result.statusToken || '');
        if (this.smsStatusToken) {
          this.writeStorage(this.statusTokenStorageKey(), this.smsStatusToken, 'local');
        }
        this.persistSmsCaptureContext(context);
        this.clearSmsEligibility();
        this.transientEmail = '';
        this.transientPhone = '';
        this.emit('accepted', {
          source_event: 'shopify_sms_consent',
          trigger: this.currentTrigger || 'unknown',
          step_name: 'sms',
          consent_mode: 'sms_double_opt_in',
        });
        // Accepted capture and a Shopify state echo are not consent authority.
        // Only the signed status endpoint's verified subscribed flag may emit it.
        if (result.rewardCode && result.rewardExpiresAt) {
          return this.activateReward(
            result.rewardCode,
            result.rewardExpiresAt,
            result.serverNow,
          );
        }
        this.emit('pending_double_opt_in', {
          source_event: 'shopify_sms_consent',
          trigger: this.currentTrigger || 'unknown',
          step_name: 'sms',
          consent_mode: 'sms_double_opt_in',
        });
        this.setJourneyStage('pending');
        this.rememberCapture();
        this.beginSmsStatusPolling();
        return true;
      } catch (error) {
        this.emit('error', {
          source_event: 'shopify_sms_consent',
          step_name: 'sms',
          consent_mode: 'sms_double_opt_in',
          error_code: error?.name === 'AbortError'
            ? 'sms_consent_request_aborted'
            : 'sms_consent_request_failed',
        });
        this.retireJourney();
        return false;
      } finally {
        this.smsSubmissionPending = false;
      }
    }

    // The consent POST and the status POST must never share an abort channel: a
    // routine poll would otherwise cancel a submission the server already accepted.
    async requestSms(url, options, channel = 'smsController') {
      if (!url) throw new Error('Missing SMS consent URL.');
      this[channel]?.abort();
      const controller = new AbortController();
      this[channel] = controller;
      try {
        return await fetch(url, {...options, signal: controller.signal});
      } finally {
        if (this[channel] === controller) this[channel] = null;
      }
    }

    wait(delayMs) {
      return new Promise((resolve) => window.setTimeout(resolve, delayMs));
    }

    smsStatusRequestOptions() {
      return {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          exposureId: this.smsExposureId,
          statusToken: this.smsStatusToken,
        }),
      };
    }

    beginSmsStatusPolling() {
      if (!this.smsExposureId || !this.dataset.smsStatusUrl || !this.smsStatusToken) return;
      window.clearTimeout(this.smsStatusTimer);
      this.smsStatusPollCount = 0;
      this.smsStatusTimer = window.setTimeout(
        () => void this.pollSmsStatus(),
        this.smsStatusPollDelayMs(),
      );
    }

    smsStatusPollDelayMs() {
      // Keep the first two minutes responsive, then back off while the shopper
      // completes confirmation. The bounded schedule covers about 30 minutes.
      return this.smsStatusPollCount < 15 ? SMS_STATUS_POLL_MS : 30000;
    }

    async pollSmsStatus() {
      if (
        !this.smsExposureId
        || this.smsStatusPollCount >= SMS_STATUS_MAX_POLLS
        || this.checkoutSuppressed
      ) return;
      this.smsStatusPollCount += 1;
      try {
        const response = await this.requestSms(
          this.dataset.smsStatusUrl,
          this.smsStatusRequestOptions(),
          'smsPollController',
        );
        if ([401, 403, 404, 410].includes(Number(response.status))) {
          this.retireJourney();
          return;
        }
        const result = await response.json();
        if (response.ok && result.subscribed === true) {
          // subscribed is the authoritative acquisition conversion. It is emitted
          // once per exposure, never once per poll, and the marker survives reloads.
          this.recordSmsSubscribed('shopify_sms_consent_status');
        }
        if (response.ok && result.emailConfirmationRequired === true) {
          this.emailConfirmationRequired = true;
          this.syncLauncherForStage('pending');
        }
        if (
          response.ok
          && result.rewardActive === true
          && result.rewardCode
          && result.rewardExpiresAt
        ) {
          this.activateReward(
            result.rewardCode,
            result.rewardExpiresAt,
            result.serverNow,
          );
          return;
        }
        if (response.ok && result.rewardExpired === true) {
          this.expireReward();
          return;
        }
        if (
          response.ok
          && (
            result.confirmationExpired === true
            || String(result.creditStatus || '') === 'CONFIRMATION_EXPIRED'
          )
        ) {
          this.emit('error', {
            source_event: 'shopify_sms_consent_status',
            step_name: 'sms',
            consent_mode: 'sms_double_opt_in',
            error_code: 'sms_confirmation_expired',
          });
          this.retireJourney();
          return;
        }
        if (response.ok && result.emailConfirmationExpired === true) {
          this.emit('error', {
            source_event: 'shopify_sms_consent_status',
            step_name: 'email',
            consent_mode: 'email_double_opt_in',
            error_code: 'email_confirmation_expired',
          });
          this.retireJourney();
          return;
        }
        if (response.ok && ['FAILED', 'SUPPRESSED'].includes(String(result.creditStatus || ''))) {
          this.emit('error', {
            source_event: 'shopify_sms_consent_status',
            step_name: 'sms',
            consent_mode: 'sms_double_opt_in',
            error_code: String(result.reasonCode || 'sms_reward_failed'),
          });
          this.retireJourney();
          return;
        }
      } catch (error) {
        // A temporary status failure must not change consent or issue another reward.
      }
      // Only a live element reschedules, so a disconnect cannot leave an
      // uncancellable chain running or start a second one on reconnect.
      if (!this.bound || this.checkoutSuppressed) return;
      window.clearTimeout(this.smsStatusTimer);
      this.smsStatusTimer = window.setTimeout(
        () => void this.pollSmsStatus(),
        this.smsStatusPollDelayMs(),
      );
    }

    clearSmsExposure() {
      this.smsExposureId = '';
      this.subscribedEmitted = false;
      this.smsStatusToken = '';
      try {
        window.localStorage.removeItem(this.dataset.smsExposureStorageKey);
        const subscribedKey = this.subscribedStorageKey();
        if (subscribedKey) window.localStorage.removeItem(subscribedKey);
        const tokenKey = this.statusTokenStorageKey();
        if (tokenKey) window.localStorage.removeItem(tokenKey);
        const contextKey = this.smsCaptureContextStorageKey();
        if (contextKey) window.localStorage.removeItem(contextKey);
      } catch (error) {
        // Exposure identifiers contain no contact data and expire with the capture window.
      }
    }

    smsCaptureContextStorageKey() {
      const key = this.dataset.smsExposureStorageKey;
      return key ? key + '-context' : '';
    }

    persistSmsCaptureContext(context) {
      this.writeStorage(this.smsCaptureContextStorageKey(), JSON.stringify({
        exposureId: this.smsExposureId,
        formId: context.formId,
        designId: context.designId,
      }), 'local');
    }

    restoreSmsCaptureContext() {
      try {
        const saved = JSON.parse(this.readStorage(this.smsCaptureContextStorageKey(), 'local'));
        if (!saved || saved.exposureId !== this.smsExposureId) return false;
        const context = this.resolveFormContext(saved.formId);
        if (context.channel !== 'sms' || context.designId !== saved.designId) return false;
        this.setActiveFormContext(context);
        return true;
      } catch (error) {
        return false;
      }
    }

    emitStepViewed(stepName, sourceEvent) {
      const key = `viewed:${stepName}`;
      if (this.emittedStepSubmissions.has(key)) return;
      this.emittedStepSubmissions.add(key);
      this.emit('step_viewed', {
        source_event: sourceEvent,
        trigger: this.currentTrigger || 'unknown',
        step_name: stepName,
      });
    }

    resolveStepName(metaData = {}) {
      return metaData.$step_name
        || metaData.stepName
        || metaData.step_name
        || (this.journeyState.stage === 'sms' ? '$15 CREDIT' : 'EMAIL');
    }

    // Owner, 2026-10-24: one block everywhere. Every page and device uses the same
    // email form, and the switched-off phone step its one popup.
    resolveFormContext(explicitFormId = '', requestedChannel = '') {
      const formId = String(explicitFormId || '');
      const channel = requestedChannel
        || (formId && formId === this.dataset.globalSmsFormId ? 'sms' : '')
        || (formId ? 'email' : this.journeyState.stage === 'sms' ? 'sms' : 'email');
      const sms = channel === 'sms';
      const prefix = sms ? 'globalSms' : 'global';
      return {
        formId: this.dataset[`${prefix}FormId`],
        formVersionId: this.dataset[`${prefix}FormVersionId`],
        designId: this.dataset[`${prefix}DesignId`],
        popupVersion: sms ? this.dataset.smsPopupVersion
          : this.dataset.emailPopupVersion || this.dataset.popupVersion,
        offerId: sms ? this.dataset.smsOfferId : this.dataset.emailOfferId || this.dataset.offerId,
        channel,
        formType: 'popup',
        placement: 'global_exit',
      };
    }

    setActiveFormContext(context) {
      this.activeFormId = context.formId || '';
      this.activeFormVersionId = context.formVersionId || '';
      this.activeDesignId = context.designId || '';
      this.activePopupVersion = context.popupVersion || this.dataset.popupVersion || '';
      this.activeOfferId = context.offerId || this.dataset.offerId || '';
      this.activeFormChannel = context.channel || 'email';
      this.activeFormType = context.formType || 'popup';
      this.activePlacement = context.placement || 'global_exit';
    }

    isOwnedForm(formId) {
      return [
        String(this.dataset.globalFormId || ''),
        String(this.dataset.globalSmsFormId || ''),
      ].filter(Boolean).includes(String(formId || ''));
    }

    showLauncher() {
      // Owner, 2026-10-10: nothing from the welcome capture shows on the cart page.
      // Journey, reward and SMS state are kept and resume on the next page. In the
      // email stage the launcher is only ever the tab, and never while the block
      // is open: the block's own heading and × stand in for it.
      const emailStage = this.journeyState.stage === 'email' && !this.emailIdentityReturnReady;
      if (this.nativeFormOpen || this.dataset.pageType === 'cart'
        || (emailStage && !this.usesOfferTab())) {
        this.hidden = true;
        this.launcher.hidden = true;
        return;
      }

      this.hidden = false;
      this.launcher.hidden = false;
      this.setAttribute('presented', '');
      this.syncLauncherForStage(this.journeyState.stage);
      this.syncAttachedStackHeight();
      window.requestAnimationFrame?.(() => this.syncAttachedStackHeight());
      this.syncSurfaceVisibility();
    }

    hideLauncher() {
      this.removeAttribute('presented');
      this.hidden = true;
      this.launcher.hidden = true;
      this.setLauncherExpanded(false);
      this.clearAttachedStackHeight();
    }

    setLauncherExpanded(expanded) {
      this.launcher?.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      this.toggleAttribute('open', expanded);
    }

    syncLauncherForStage(stage) {
      const content = {
        qualifying: this.emailProofPending
          ? (this.emailIdentityRequired ? ['SIGN IN', 'FOR $15 BY TEXT'] : ['CHECK YOUR EMAIL', 'THEN TAP HERE'])
          : [this.dataset.smsQualifyingLauncherKicker, this.dataset.smsQualifyingLauncherValue],
        sms: [this.dataset.smsLauncherKicker, this.dataset.smsLauncherValue],
        pending: this.emailConfirmationRequired
          ? [this.dataset.emailPendingLauncherKicker, this.dataset.emailPendingLauncherValue]
          : [this.dataset.smsPendingLauncherKicker, this.dataset.smsPendingLauncherValue],
        reward: [
          '$15 CREDIT',
          this.rewardCountdownLabel(),
        ],
        email: this.emailIdentityReturnReady
          ? ['CONTINUE', '$15 BY TEXT']
          : [this.dataset.defaultLauncherKicker, this.dataset.defaultLauncherValue],
      }[stage] || [this.dataset.defaultLauncherKicker, this.dataset.defaultLauncherValue];

      if (this.launcherKicker) this.launcherKicker.textContent = content[0] || '';
      if (this.launcherValue) this.launcherValue.textContent = content[1] || '';
      if (!content[0]) this.launcher?.setAttribute('data-single-label', '');
      else this.launcher?.removeAttribute?.('data-single-label');
      const emailConfirmationPending = this.emailConfirmationRequired && stage === 'pending';
      const temporarilyDisabled = emailConfirmationPending || (stage === 'qualifying' && !this.emailProofPending);
      if (this.launcherIcon) {
        const symbol = stage === 'reward' ? 'reward' : temporarilyDisabled ? 'pending' : 'toggle';
        this.launcherIcon.setAttribute?.('data-symbol', symbol);
        const statusIcon = this.launcherIcon.querySelector?.('[data-nw-offer-icon-status]');
        const statusText = stage === 'reward'
          ? '→'
          : temporarilyDisabled
            ? '•'
            : '+';
        // Preserve the theme's SVG plus/close controls through journey updates.
        // Older markup keeps its text fallback until the matching theme deploys.
        if (statusIcon) statusIcon.textContent = symbol === 'toggle' ? '' : statusText;
        else this.launcherIcon.textContent = statusText;
      }
      this.toggleAttribute('data-offer-tab', stage === 'email' && this.usesOfferTab());
      if (this.launcherTab) this.launcherTab.textContent = this.dataset.offerTabLabel || '10% OFF';
      this.launcher?.setAttribute('aria-disabled', temporarilyDisabled ? 'true' : 'false');
      if (this.launcher) this.launcher.disabled = temporarilyDisabled;
      this.dataset.stage = stage;
      // WCAG 2.5.3: the accessible name has to contain the visible label, or
      // voice control cannot address the only control the theme owns.
      this.launcher?.setAttribute(
        'aria-label',
        [content[0], content[1]].filter(Boolean).join(' '),
      );
    }

    usesAttachedLauncher() {
      // Gate on the dock's own compact query, not the narrower mobile test, so a
      // docked launcher is never left floating over the purchase panel.
      return this.dataset.pageType === 'product'
        && this.parentElement?.matches?.('[data-mobile-sticky-offer-slot]') === true
        && window.matchMedia('(max-width: 989px), (any-pointer: coarse)').matches;
    }

    syncAttachedStackHeight() {
      const rootStyle = document.documentElement?.style;
      if (!rootStyle) return;
      // The closed tab sits beside the chat bubble, so it reserves no stack.
      if (this.hasAttribute('data-offer-tab') && !this.hasAttribute('open')) {
        rootStyle.setProperty('--nw-floating-offer-stack-height', '0px');
        rootStyle.setProperty('--nw-first-order-offer-stack-height', '0px');
        return;
      }
      const floatingHeight = !this.usesAttachedLauncher() && !this.hidden && !this.launcher?.hidden
        && !this.hasAttribute('data-surface-suspended')
        ? Math.ceil(this.getBoundingClientRect?.().height || 0) : 0;
      if (!this.usesAttachedLauncher() || this.hidden || this.launcher?.hidden
        || this.hasAttribute('data-surface-suspended')) {
        this.clearAttachedStackHeight();
        rootStyle.setProperty('--nw-floating-offer-stack-height', `${floatingHeight}px`);
        return;
      }
      rootStyle.setProperty('--nw-floating-offer-stack-height', '0px');

      const height = Math.ceil(this.getBoundingClientRect?.().height || 0)
        || Math.ceil(this.launcher?.getBoundingClientRect?.().height || 0);
      if (height > 0) rootStyle.setProperty('--nw-first-order-offer-stack-height', `${height}px`);
    }

    clearAttachedStackHeight() {
      document.documentElement?.style?.removeProperty('--nw-floating-offer-stack-height');
      document.documentElement?.style?.removeProperty('--nw-first-order-offer-stack-height');
    }

    // The event schema pins low-cardinality tokens. Normalising here keeps every
    // producer on one vocabulary instead of trusting each call site.
    schemaToken(value, fallback) {
      const token = String(value || '')
        .toLocaleLowerCase('en-US')
        .replace(/[^a-z0-9_]+/g, '_')
        .replace(/^_+|_+$/g, '');
      return token.length >= 2 ? token.slice(0, 80) : fallback;
    }

    schemaPageType() {
      const pageType = this.dataset.pageType === 'index'
        ? 'home'
        : String(this.dataset.pageType || '');
      return SCHEMA_PAGE_TYPES.has(pageType) ? pageType : 'other';
    }

    schemaStepName(value) {
      const step = String(value || '').toLocaleLowerCase('en-US');
      if (SCHEMA_STEP_NAMES.has(step)) return step;
      if (step === 'pending') return 'sms';
      if (step === 'qualifying') return 'email';
      if (['confirmation_required', 'complete', 'reward'].includes(step)) return 'success';
      return 'not_applicable';
    }

    schemaConsentMode(value) {
      const mode = String(value || '').toLocaleLowerCase('en-US');
      if (SCHEMA_CONSENT_MODES.has(mode)) return mode;
      if (mode === 'email_double_opt_in_sms_double_opt_in') return 'email_then_sms';
      return 'unknown';
    }

    smsStepEnabled() {
      return this.dataset.smsStepEnabled === 'true';
    }

    isEligiblePage() {
      const audienceEligible = this.dataset.captureEligible === 'true'
        || (this.isSmsCampaignIntent() && this.dataset.smsCampaignEligible === 'true');
      const allowedSurface = ALLOWED_PAGE_TYPES.includes(this.dataset.pageType)
        || (this.dataset.pageType === 'page' && this.dataset.socialLinksPage === 'true');
      return audienceEligible && allowedSurface;
    }

    currentKnownCartCount() {
      const drawer = document.querySelector('cart-drawer');
      if (drawer) return Number.parseInt(drawer.dataset.cartItemCount, 10) || 0;
      return Number.parseInt(this.dataset.initialCartCount, 10) || 0;
    }

    async getCart() {
      const response = await this.request(this.dataset.cartJsonUrl, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Cart request failed with status ${response.status}.`);
      return response.json();
    }

    isEligibleCart(cart) {
      return !this.hasDiscountCode(cart);
    }

    isSmsCampaignIntent() {
      try {
        return this.smsStepEnabled()
          && (new URL(window.location.href).searchParams.get('nw_offer') === 'sms15'
            || this.authReturnContext?.campaign === true);
      } catch (error) {
        return false;
      }
    }

    hasDiscountCode(cart) {
      const cartApplications = Array.isArray(cart?.cart_level_discount_applications)
        ? cart.cart_level_discount_applications
        : [];
      const lineApplications = Array.isArray(cart?.items)
        ? cart.items.flatMap((item) => (
            Array.isArray(item.line_level_discount_allocations)
              ? item.line_level_discount_allocations
                  .map((allocation) => allocation.discount_application)
                  .filter(Boolean)
              : []
          ))
        : [];

      const discountCodes = Array.isArray(cart?.discount_codes)
        ? cart.discount_codes.map(entry => ({type: 'discount_code',
          title: typeof entry === 'string' ? entry : entry?.code})) : [];
      return [...cartApplications, ...lineApplications, ...discountCodes].some((application) => (
        application?.type === 'discount_code'
        && !(this.isSmsCampaignIntent() && String(application.title || '').toUpperCase() === 'WELCOME10')
      ));
    }

    currentProductIsOnSale() {
      return this.dataset.currentProductOnSale === 'true';
    }

    cartHasSale(cart) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer?.dataset?.cartHasSale) return drawer.dataset.cartHasSale === 'true';
      if (this.dataset.initialCartHasSale === 'true') return true;
      return Array.isArray(cart?.items) && cart.items.some((item) => (
        Number(item.compare_at_price || 0) > Number(item.original_price || item.price || 0)
      ));
    }

    isCookieCardVisible() {
      if (document.documentElement.classList.contains('nw-cookie-card-visible')) return true;
      // Shopify can insert its privacy card before the theme's visibility
      // bridge has discovered it. Respect the actual rendered card as well.
      const banner = document.querySelector('#shopify-pc__banner');
      if (!banner || banner.closest?.('[hidden], [aria-hidden="true"]')) return false;
      const rect = banner.getBoundingClientRect?.();
      const style = window.getComputedStyle?.(banner);
      return rect?.width > 0 && rect?.height > 0
        && style?.display !== 'none' && style?.visibility !== 'hidden'
        && Number.parseFloat(style?.opacity || '1') > 0;
    }

    hasBlockingSurface() {
      if (this.isCookieCardVisible()) return true;
      if (document.querySelector('cart-drawer[open], cart-drawer[closing], header-drawer details[open], details-modal details[open], [data-mega-menu-details][open], modal-dialog[open], dialog[open]')) return true;
      const active = document.activeElement;
      if (active?.matches?.('input:not([type="hidden"]), textarea, select, [contenteditable="true"]')
        && !active.closest?.('[data-testid^="klaviyo-form-"]')?.closest?.('[role="dialog"]')) return true;
      // Exclude this journey's native dialog and the hidden cart dialog. Other
      // visible modal forms and Intercom's expanded messenger take priority.
      return Array.from(document.querySelectorAll?.('[role="dialog"][aria-modal="true"], .intercom-messenger-frame, .intercom-lightweight-app-messenger') || []).some((surface) => {
        if (surface.closest?.('[hidden], [aria-hidden="true"]')) return false;
        if (surface.querySelector?.(`[data-testid="klaviyo-form-${this.activeFormId}"]`)) return false;
        const rect = surface.getBoundingClientRect?.();
        const style = window.getComputedStyle?.(surface);
        return rect?.width > 0 && rect?.height > 0
          && style?.display !== 'none' && style?.visibility !== 'hidden';
      });
    }

    scheduleSurfaceReconciliation() {
      if (this.surfaceTimer) return;
      this.surfaceTimer = window.setTimeout(() => {
        this.surfaceTimer = null;
        this.syncSurfaceState();
      }, 80);
    }

    syncSurfaceVisibility() {
      const blocked = this.hasBlockingSurface();
      if (blocked !== this.hasAttribute('data-surface-suspended')) {
        this.toggleAttribute('data-surface-suspended', blocked);
        this.toggleAttribute('inert', blocked);
        this.syncAttachedStackHeight();
      }
      return blocked;
    }

    syncSurfaceState() {
      // Klaviyo can emit its open receipt before inserting the native renderer.
      // Reconcile after DOM changes so late frames are docked at full tap size.
      this.syncNativeAnchor();
      if (this.syncSurfaceVisibility()) {
        if (this.nativeFormOpen && !this.closingForSurface) {
          this.closingForSurface = true;
          if (!this.closeNativeForm()) this.closingForSurface = false;
        }
        return;
      }
      if (document.visibilityState === 'hidden' || this.checkoutSuppressed) return;
      if (this.deferredNativeOpen) {
        const pending = this.deferredNativeOpen;
        this.deferredNativeOpen = null;
        this.openNativeForm(pending);
      } else if (this.deferredReason) {
        const reason = this.deferredReason;
        const triggerValue = this.deferredTriggerValue;
        this.deferredReason = null;
        this.deferredTriggerValue = '';
        void this.tryPresent(reason, {triggerValue});
      }
    }

    deferUntilCookieResolved(reason, triggerValue) {
      const currentPriority = TRIGGER_PRIORITY[this.deferredReason] || 0;
      const nextPriority = TRIGGER_PRIORITY[reason] || 0;
      if (!this.deferredReason || nextPriority >= currentPriority) {
        this.deferredReason = reason;
        this.deferredTriggerValue = triggerValue;
      }
    }

    claimJourneySlot(reason) {
      if (window.NordwegJourneyCoordinator?.claim) {
        return window.NordwegJourneyCoordinator.claim('first-order-offer', { reason });
      }

      try {
        const winner = window.sessionStorage.getItem(SHARED_INTERRUPTION_KEY);
        if (winner && winner !== 'first-order-offer') return false;
        if (!winner) window.sessionStorage.setItem(SHARED_INTERRUPTION_KEY, 'first-order-offer');
        return true;
      } catch (error) {
        if (this.sharedJourneySlotClaimed) return true;
        this.sharedJourneySlotClaimed = true;
        return true;
      }
    }

    async request(url, options) {
      if (!url) throw new Error('Missing request URL.');
      this.activeController?.abort();
      const controller = new AbortController();
      this.activeController = controller;
      try {
        return await fetch(url, { ...options, signal: controller.signal });
      } finally {
        if (this.activeController === controller) this.activeController = null;
      }
    }

    emit(action, details = {}) {
      if (!ALLOWED_ACTIONS.has(action)) return null;
      const context = this.activeFormId ? {
        formId: this.activeFormId,
        formVersionId: this.activeFormVersionId,
        designId: this.activeDesignId,
        popupVersion: this.activePopupVersion,
        offerId: this.activeOfferId,
        channel: this.activeFormChannel,
        formType: this.activeFormType,
        placement: this.activePlacement,
      } : this.resolveFormContext();
      const eventId = this.uuid();
      const payload = {
        schema_version: SCHEMA_VERSION,
        action,
        source_system: SOURCE_SYSTEM,
        source_event: String(details.source_event || 'theme').slice(0, 80),
        popup_id: this.dataset.popupId || '',
        popup_version: context.popupVersion || this.dataset.popupVersion || '',
        design_id: context.designId || '',
        delivery_mode: 'klaviyo_native',
        klaviyo_form_id: context.formId || '',
        klaviyo_form_version_id: context.formVersionId || '',
        form_type: context.formType || '',
        placement: context.placement || '',
        page_type: this.schemaPageType(),
        page_handle: String(this.dataset.pageHandle || '').slice(0, 100),
        product_id: String(this.dataset.productId || '').slice(0, 40),
        collection_id: String(this.dataset.collectionId || '').slice(0, 40),
        trigger: this.schemaToken(details.trigger || this.currentTrigger, 'unknown'),
        trigger_value: this.schemaToken(details.trigger_value || this.currentTriggerValue, ''),
        audience_rule: this.dataset.audienceRule || '',
        offer_id: context.offerId || this.dataset.offerId || '',
        variant_id: this.dataset.variantId || 'control',
        step_name: this.schemaStepName(details.step_name || this.journeyState.stage),
        device_class: this.deviceClass(),
        consent_mode: this.schemaConsentMode(details.consent_mode || this.dataset.consentMode),
        sms_provider: this.dataset.smsProvider || '',
        sms_confirmation_mode: this.dataset.smsConfirmationMode || '',
        utm_source: this.queryValue('utm_source'),
        utm_medium: this.queryValue('utm_medium'),
        utm_campaign: this.queryValue('utm_campaign'),
        utm_id: this.queryValue('utm_id'),
        referrer_class: this.referrerClass(),
        error_code: this.schemaToken(details.error_code, '') || null,
        exposure_id: this.exposureId || '',
        event_id: eventId,
        occurred_at: new Date().toISOString(),
        session_id: this.sessionId,
        page_path: this.safeAnalyticsDimension(window.location.pathname, 300, '/'),
      };

      if (typeof window.CustomEvent === 'function') {
        document.dispatchEvent(new window.CustomEvent('nw:popup-interaction', { detail: payload }));
        document.dispatchEvent(new window.CustomEvent('nw:first-order-offer', {
          detail: { action, trigger: payload.trigger, formId: payload.klaviyo_form_id },
        }));
      }

      // The consent-gated commerce router publishes the owned data-layer and
      // Shopify copies when present. Keep a single direct fallback on pages
      // without that router, and never publish analytics before consent.
      let analyticsAllowed = false;
      try {
        analyticsAllowed = window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
      } catch (error) {
        // An unavailable privacy API is not permission to publish analytics.
      }
      if (analyticsAllowed && !window.NordwegCommerceMeasurement?.eventVersion) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: TRACK_EVENT_NAME, ...payload });
      }

      if (this.identityReady) {
        this.trackWithKlaviyo(payload);
      } else {
        this.pendingKlaviyoEvents.push(payload);
        if (this.pendingKlaviyoEvents.length > 40) this.pendingKlaviyoEvents.shift();
      }
      return payload;
    }

    emitCommercial(eventName, details = {}) {
      const payload = {
        source_system: SOURCE_SYSTEM,
        source_event: 'shopify_sms_consent_status',
        event_id: this.uuid(),
        occurred_at: new Date().toISOString(),
        device_class: this.deviceClass(),
        placement: this.activePlacement || this.resolveFormContext().placement,
        ...details,
      };
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({event: eventName, ...payload});
      this.trackWithKlaviyo(payload, eventName);
      return payload;
    }

    scheduleKlaviyoFlush() {
      window.clearTimeout(this.flushTimer);
      this.flushTimer = window.setTimeout(() => this.flushKlaviyoEvents(), 250);
    }

    flushKlaviyoEvents() {
      if (!this.identityReady || !this.pendingKlaviyoEvents.length) return;
      const pending = this.pendingKlaviyoEvents.splice(0);
      pending.forEach((payload) => this.trackWithKlaviyo(payload));
    }

    trackWithKlaviyo(payload, eventName = TRACK_EVENT_NAME) {
      if (window.klaviyo && typeof window.klaviyo.track === 'function') {
        Promise.resolve(window.klaviyo.track(eventName, payload)).catch(() => {});
        return;
      }
      if (Array.isArray(window._learnq)) {
        window._learnq.push(['track', eventName, payload]);
      }
    }

    deviceClass() {
      if (window.matchMedia('(max-width: 749px)').matches) return 'mobile';
      if (window.matchMedia('(max-width: 989px), (any-pointer: coarse)').matches) return 'tablet';
      return 'desktop';
    }

    safeAnalyticsDimension(value, maxLength = 100, fallback = '') {
      const normalized = String(value || '').trim().slice(0, maxLength);
      if (!normalized) return fallback;

      let inspected = normalized;
      try {
        inspected = decodeURIComponent(normalized);
      } catch (error) {
        // Inspect the bounded original when malformed escaping prevents decoding.
      }

      const containsEmail = /[^\s/@]+@[^\s/@]+\.[^\s/@]+/.test(inspected);
      const containsUrl = /(?:https?:\/\/|www\.)/i.test(inspected);
      const containsIpv4 = /(?:^|[^0-9])(?:\d{1,3}\.){3}\d{1,3}(?:[^0-9]|$)/.test(inspected);
      const containsNanpPhone = /(?:^|[^0-9])(?:\+?1[\s().-]*)?[2-9]\d{2}[\s().-]*[2-9]\d{2}[\s.-]*\d{4}(?:[^0-9]|$)/.test(inspected);
      const containsControlCharacter = /[\u0000-\u001f\u007f]/.test(inspected);

      if (containsEmail || containsUrl || containsIpv4 || containsNanpPhone || containsControlCharacter) {
        return fallback;
      }
      return normalized;
    }

    queryValue(key) {
      try {
        return this.safeAnalyticsDimension(
          new URLSearchParams(window.location.search).get(key),
          100,
          '',
        );
      } catch (error) {
        return '';
      }
    }

    referrerClass() {
      if (!document.referrer) return 'direct';
      try {
        const domain = new URL(document.referrer).hostname.toLocaleLowerCase('en-US');
        if (domain === window.location.hostname) return 'same_origin';
        if (domain.includes('google.')) return 'google';
        if (domain.includes('facebook.') || domain.includes('instagram.')) return 'meta';
        if (domain.includes('mail.') || domain.includes('email.')) return 'email';
        return 'other';
      } catch (error) {
        return 'other';
      }
    }

    setJourneyStage(stage) {
      this.journeyState = { stage, updatedAt: Date.now() };
      this.syncLauncherForStage(stage);
      this.writeStorage(this.dataset.sessionStorageKey, JSON.stringify(this.journeyState), 'session');
      this.writeStorage(this.dataset.sessionStorageKey, JSON.stringify(this.journeyState), 'local');
    }

    readJourneyState() {
      const parse = (value) => {
        if (!value) return null;
        try {
          const state = JSON.parse(value);
          if (!['email', 'qualifying', 'sms', 'pending', 'reward', 'complete'].includes(state.stage)) return null;
          if (state.updatedAt && Date.now() - Number(state.updatedAt) > CAPTURE_SUPPRESSION_MS) return null;
          return state;
        } catch (error) {
          return null;
        }
      };

      return parse(this.readStorage(this.dataset.sessionStorageKey, 'session'))
        || parse(this.readStorage(this.dataset.sessionStorageKey, 'local'))
        || { stage: 'email', updatedAt: Date.now() };
    }

    rememberShown() {
      this.shownInMemory = true;
      this.writeStorage(this.dataset.shownStorageKey, String(Date.now()), 'session');
    }

    wasShownThisSession() {
      return this.shownInMemory
        || Number(this.readStorage(this.dataset.shownStorageKey, 'session')) > 0;
    }

    rememberDismissal() {
      this.clearEmailIdentityReturn();
      const now = Date.now();
      this.dismissedAtInMemory = now;
      this.writeStorage(this.dataset.dismissedStorageKey, String(now), 'local');
    }

    // A close is remembered past the 14 quiet days: it keeps the tab until sign-up.
    hasDismissed() {
      return Number(this.dismissedAtInMemory) > 0
        || Number(this.readStorage(this.dataset.dismissedStorageKey, 'local')) > 0;
    }

    wasDismissedRecently() {
      return this.hasRecentInMemoryTimestamp(this.dismissedAtInMemory, DISMISS_SUPPRESSION_MS)
        || this.hasRecentTimestamp(this.dataset.dismissedStorageKey, DISMISS_SUPPRESSION_MS);
    }

    rememberCapture() {
      this.clearEmailIdentityReturn();
      this.capturedAtInMemory = Date.now();
      this.writeStorage(this.dataset.capturedStorageKey, String(Date.now()), 'local');
    }

    wasCapturedRecently() {
      return this.hasRecentInMemoryTimestamp(this.capturedAtInMemory, CAPTURE_SUPPRESSION_MS)
        || this.hasRecentTimestamp(this.dataset.capturedStorageKey, CAPTURE_SUPPRESSION_MS);
    }

    rememberCheckoutStarted() {
      this.checkoutStartedAtInMemory = Date.now();
      this.writeStorage(this.dataset.checkoutStartedStorageKey, String(Date.now()), 'local');
      this.hardSuppressAfterCheckout();
    }

    hardSuppressIfCheckoutStarted() {
      if (this.checkoutStartedRecently()) this.hardSuppressAfterCheckout();
    }

    hardSuppressAfterCheckout() {
      this.clearEmailIdentityReturn();
      this.checkoutSuppressed = true;
      window.clearTimeout(this.visitTimer);
      window.clearTimeout(this.formOpenTimer);
      window.clearTimeout(this.smsStatusTimer);
      window.clearTimeout(this.rewardTimer);
      window.clearTimeout(this.submitReceiptTimer);
      this.smsStatusTimer = null;
      this.rewardTimer = null;
      this.activeController?.abort();
      if (!this.smsSubmissionPending) this.smsController?.abort();
      this.smsPollController?.abort();
      this.rewardStatusController?.abort();
      this.clearSmsEligibility();
      this.hideLauncher();
      if (typeof window.CustomEvent === 'function') {
        document.dispatchEvent(new window.CustomEvent('nw:commerce-journey-checkout-started'));
      }
    }

    checkoutStartedRecently() {
      return this.hasRecentInMemoryTimestamp(this.checkoutStartedAtInMemory, RETURN_WINDOW_MS)
        || this.hasRecentTimestamp(this.dataset.checkoutStartedStorageKey, RETURN_WINDOW_MS);
    }

    hasRecentTimestamp(key, windowMs) {
      const timestamp = Number(this.readStorage(key, 'local'));
      return timestamp > 0 && Date.now() - timestamp < windowMs;
    }

    hasRecentInMemoryTimestamp(timestamp, windowMs) {
      return Number(timestamp) > 0 && Date.now() - Number(timestamp) < windowMs;
    }

    readOrCreateSessionId() {
      const key = this.dataset.sessionIdStorageKey;
      const existing = this.readStorage(key, 'session');
      if (existing) return existing;
      const value = this.uuid();
      this.writeStorage(key, value, 'session');
      return value;
    }

    readStorage(key, type) {
      if (!key) return '';
      try {
        const storage = type === 'local' ? window.localStorage : window.sessionStorage;
        return storage.getItem(key) || '';
      } catch (error) {
        return '';
      }
    }

    writeStorage(key, value, type) {
      if (!key) return;
      try {
        const storage = type === 'local' ? window.localStorage : window.sessionStorage;
        storage.setItem(key, value);
      } catch (error) {
        // Storage improves continuity but never blocks the native form.
      }
    }

    uuid() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();
      return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    }
  }

  registry.define(ELEMENT_NAME, NordwegFirstOrderExitOffer);
})();
